import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { accountsApi } from '../accounts/api/accounts';
import AccountSearchInput from '../../components/AccountSearchInput';
import { contactsApi } from '../contacts/api/contacts';
import { pipelineApi as dealsApi } from '../../modules/pipeline/api/pipelineApi';
import { userManager } from '../login/api/auth';
import { buildEnquiryKpis } from './kpi';
import { enquiriesApi } from '../../modules/enquiries/api/enquiriesApi';
import { priceNegotiationsApi } from '../sales-pipeline/api/price-negotiations';
import { usersApi } from '../users/api/users';
import { exportApi } from '../../core/api/exportApi';

// ─── Enquiry draft autosave (localStorage) ──────────────────────────────
const DRAFT_KEY = 'alokcrm_enquiry_draft_v1';
const DRAFT_MAX_AGE_MS = 24 * 60 * 60 * 1000;

const draftStore = {
  read() {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (!raw) return null;
      const obj = JSON.parse(raw);
      if (!obj || !obj.savedAt) return null;
      if (Date.now() - obj.savedAt > DRAFT_MAX_AGE_MS) {
        localStorage.removeItem(DRAFT_KEY);
        return null;
      }
      return obj;
    } catch (e) {
      return null;
    }
  },
  write(payload) {
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify({ ...payload, savedAt: Date.now() }));
    } catch (e) {}
  },
  clear() {
    try { localStorage.removeItem(DRAFT_KEY); } catch (e) {}
  },
};

const SALE_TYPES = [
  { id: 'LOCAL', label: 'Local' },
  { id: 'EXPORT', label: 'Export' },
];

const LEAD_SOURCES = [
  'Inbound Inquiry',
  'Client Reference',
  'Email',
  'WhatsApp',
  'Website',
  'Referral',
  'Other',
];

const LOCAL_DROPDOWNS = {
  product: ['Bright Bar', 'Round Bar', 'Hex Bar', 'Square Bar', 'Flat Bar'],
  shape: ['ROUND', 'HEX', 'SQUARE', 'FLAT'],
  sizeMm: ['6', '8', '10', '12', '16', '20', '25', '30', '40', '50'],
  lengthMm: ['1000', '1500', '2000', '3000', '4000', '6000'],
  weight: ['Up to 50 kg', '50-200 kg', '200-500 kg', '500-1000 kg', '1000+ kg'],
  tolerance: ['h8', 'h9', 'h10'],
  heatTreatment: ['ANNEAL', 'NORMALIZE', 'QT', 'SOFT ANNEAL'],
  chamfering: ['None', 'Single Side', 'Double Side'],
  unit: ['kg', 'tons', 'pcs'],
  ultrasonicTest: ['Required', 'Not Required'],
  surface: ['Black', 'Bright', 'Peeled', 'Ground', 'Polished'],
  incoterms: ['EXW', 'FOB', 'CIF', 'DAP', 'DDP'],
  priceOffer: ['0.5', '1', '1.5', '2', '2.5', '3', '5', '7.5', '10', '12.5', '15'],
  priceUnit: ['INR/kg', 'USD/kg', 'EUR/kg', 'INR/ton', 'USD/ton', 'EUR/ton'],
  paymentTerms: ['100% Advance', '30 Days', '45 Days', '60 Days'],
  orderProgress: ['Pending', 'Reviewed', 'Quoted', 'Won', 'Lost'],
};

const GRADE_FALLBACK = [
  '403','409','410/1.4006','410L/1.4003','415','416/1.4005','420/1.4021','420B/1.4028',
  '420C/1.4034','420D','422','430/1.4016','430F/1.4104','430Nb','431/1.4057','434',
  '440M','440C','441','444','301','303/1.4305','304L/1.4307','309/1.4824','310S',
  '314/1.4841','316L/1.4404','316LN','316Ti/1.4571','317LMN','318LN','321','321H',
  '329','347','630/1.4542','800','926','A182/F44','A182/F55','1.4114','1.4116',
  '1.412','1.4122','1.4362','1.441','1.4418','1.455','1.4571','1.4718','1.4724',
  '1.4742','1.4828','1.4835','1.4841','1.4845','1.4876','1.4878','1.4923','1.4935'
];

const localDefault = {
  saleType: 'LOCAL',
  offerNo: '',
  validityDays: '',
  followupDate: '',
  enquiryDate: new Date().toISOString().split('T')[0],
  customerName: '',
  contactPerson: '',
  email: '',
  salesperson: '',
  kamEcode: '',
  phone: '',
  leadSource: 'Inbound Inquiry',
  state: '',
  city: '',
  orderProgress: 'Pending',
  paymentTerms: '',
  packing: '',
  freight: '',
  preparedBy: '',
  deliveryTerms: '',
  port: '',
  specialNotes: '',
  approvedByEcode: '',
  deliveryNote: '',
};

const defaultProductLine = {
  product: '',
  shape: '',
  grade: '',
  specification: '',
  standard: '',
  sizeMm: '',
  lengthMm: '',
  heatTreatment: '',
  tolerance: '',
  chamfering: '',
  qty: '',
  unit: '',
  ultrasonicTest: '',
  surface: '',
  incoterms: '',
  priceOffer: '',
  priceUnit: '',
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^[0-9+\-() ]{8,20}$/;

const getShapeFromProduct = (product) => {
  const value = String(product || '').trim().toLowerCase();
  if (value.includes('round')) return 'ROUND';
  if (value.includes('hex')) return 'HEX';
  if (value.includes('square')) return 'SQUARE';
  if (value.includes('flat')) return 'FLAT';
  if (value.includes('bright')) return 'ROUND';
  return 'ROUND';
};

const INCOTERMS_VALUES = ['EXW', 'FOB', 'CIF', 'DAP', 'DDP', 'FCA', 'CFR'];

const splitIncotermsAndPayment = (item) => {
  const rawIncoterms = String(item?.Incoterms || item?.incoterms || '').trim();
  const explicitPayment = String(item?.PaymentTerms || item?.payment_terms || '').trim();

  const incotermsUpper = rawIncoterms.toUpperCase();
  const looksLikePayment =
    /\b(advance|payment|days?|credit|cad|against delivery|pdc)\b/i.test(rawIncoterms) &&
    !INCOTERMS_VALUES.includes(incotermsUpper);

  if (looksLikePayment) {
    return {
      incoterms: '-',
      paymentTerms: explicitPayment || rawIncoterms || '-',
    };
  }

  return {
    incoterms: rawIncoterms || '-',
    paymentTerms: explicitPayment || '-',
  };
};

const Enquiries = () => {
  const [showForm, setShowForm] = useState(false);
  const [saleType, setSaleType] = useState('LOCAL');
  const [localForm, setLocalForm] = useState(localDefault);
  const [productLines, setProductLines] = useState([{ ...defaultProductLine }]);
  const [enquiries, setEnquiries] = useState([]);
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [gradeOptions, setGradeOptions] = useState([]);
  const [openGradeIndex, setOpenGradeIndex] = useState(null);
  const [toleranceOptions, setToleranceOptions] = useState([]);
  const [isMastersLoading, setIsMastersLoading] = useState(false);
  const [cachedAccounts, setCachedAccounts] = useState([]);
  const [mastersError, setMastersError] = useState('');
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [closingReason, setClosingReason] = useState('');
  const [showClosePicker, setShowClosePicker] = useState(false);
  const [closingBusy, setClosingBusy] = useState(false);
  const [enqPrices, setEnqPrices] = useState([]);
  const [enqNewPrice, setEnqNewPrice] = useState('');
  const [enqNewType, setEnqNewType] = useState('COUNTER');
  const [enqNewNote, setEnqNewNote] = useState('');
  const [enqLogging, setEnqLogging] = useState(false);

  const loadEnqPrices = useCallback(async (enquiryId) => {
    if (!enquiryId) { setEnqPrices([]); return; }
    try {
      const data = await priceNegotiationsApi.getPrices({ enquiryId });
      setEnqPrices(Array.isArray(data) ? data : []);
    } catch (e) { console.error('load enq prices', e); setEnqPrices([]); }
  }, []);

  useEffect(() => {
    if (selectedEnquiry?.id) loadEnqPrices(selectedEnquiry.id);
    else setEnqPrices([]);
  }, [selectedEnquiry?.id, loadEnqPrices]);

  const handleEnqLogPrice = async () => {
    if (!enqNewPrice || !selectedEnquiry?.id) return;
    setEnqLogging(true);
    try {
      await priceNegotiationsApi.logPrice({
        enquiry_id: selectedEnquiry.id,
        account_id: selectedEnquiry.AccountID || selectedEnquiry.account_id,
        price: Number(enqNewPrice),
        price_type: enqNewType,
        note: enqNewNote || null,
      });
      setEnqNewPrice(''); setEnqNewNote(''); setEnqNewType('COUNTER');
      await loadEnqPrices(selectedEnquiry.id);
    } catch (e) { console.error('log enq price', e); }
    finally { setEnqLogging(false); }
  };
  const [editingEnquiry, setEditingEnquiry] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [accountSuggestions, setAccountSuggestions] = useState([]);
  const [stageFilter, setStageFilter] = useState('ALL');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [serverPage, setServerPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const SERVER_PAGE_SIZE = 200;
  const [draftBanner, setDraftBanner] = useState(null);
  const [dupModal, setDupModal] = useState(null); // {matches, payload, onUse, onForce}

  useEffect(() => {
    if (!showForm) return;
    if (editingEnquiry) return;
    const timer = setTimeout(() => {
      draftStore.write({ saleType, localForm, productLines });
    }, 5000);
    return () => clearTimeout(timer);
  }, [showForm, editingEnquiry, saleType, localForm, productLines]);

  useEffect(() => {
    if (!showForm) { setDraftBanner(null); return; }
    if (editingEnquiry) { setDraftBanner(null); return; }
    const existing = draftStore.read();
    if (existing && existing.productLines && existing.productLines.length > 0) {
      setDraftBanner({ savedAt: existing.savedAt });
    } else {
      setDraftBanner(null);
    }
  }, [showForm, editingEnquiry]);

  const restoreDraft = () => {
    const d = draftStore.read();
    if (!d) { setDraftBanner(null); return; }
    if (d.saleType) setSaleType(d.saleType);
    if (d.localForm) setLocalForm(d.localForm);
    if (d.productLines && d.productLines.length > 0) setProductLines(d.productLines);
    setDraftBanner(null);
  };

  const discardDraft = () => {
    draftStore.clear();
    setDraftBanner(null);
  };

  const location = useLocation();
  const enquiryIdFromQuery = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('enquiryId');
  }, [location.search]);
  const editFromQuery = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('edit') === '1';
  }, [location.search]);
  const [salesManagers, setSalesManagers] = useState([]);
  const [scUsers, setScUsers] = useState([]);
  const [isSalesManagersLoading, setIsSalesManagersLoading] = useState(false);
  const [salesManagersError, setSalesManagersError] = useState('');
  const [totalEnquiriesAll, setTotalEnquiriesAll] = useState(null);
  const [enqStatCounts, setEnqStatCounts] = useState(null);
  const [importState, setImportState] = useState({
    open: false,
    headers: [],
    rows: [],
    rawRows: [],
    headerRowIndex: 0,
    file: null,
    mapping: {
      material: null,
      grade: null,
      size: null,
      length: null,
      qty: null,
      price: null,
      unit: null,
    },
    error: '',
    filename: '',
  });

  const rows = useMemo(() => enquiries, [enquiries]);

  const filteredRows = useMemo(() => {
    if (stageFilter === 'PENDING_PRICE') {
      return rows.filter((row) => String(row.pipelineStage || '').toUpperCase() === 'PENDING_PRICE');
    }
    return rows;
  }, [rows, stageFilter]);
  const totalPages = useMemo(() => Math.max(1, Math.ceil(filteredRows.length / pageSize)), [filteredRows.length, pageSize]);
  const safePage = Math.min(currentPage, totalPages);
  const pagedRows = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [filteredRows, safePage, pageSize]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, stageFilter, pageSize]);

  useEffect(() => {
    if (currentPage !== safePage) {
      setCurrentPage(safePage);
    }
  }, [currentPage, safePage]);
  const kpiCards = useMemo(() => buildEnquiryKpis(rows, { totalOverride: totalEnquiriesAll, serverCounts: enqStatCounts }), [rows, totalEnquiriesAll, enqStatCounts]);

  const [monthFilter, setMonthFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const monthOptions = useMemo(() => {
    const out = [];
    const now = new Date();
    for (let i = 0; i < 12; i += 1) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      out.push({ value: val, label: d.toLocaleString('en-GB', { month: 'long', year: 'numeric' }) });
    }
    return out;
  }, []);
  const [dailyCounts, setDailyCounts] = useState(null);
  const isAdminUser = ['admin', 'local_admin'].includes(
    String(userManager.getUser()?.role || '').toLowerCase()
  );

  useEffect(() => {
    if (!isAdminUser) return;
    let alive = true;
    (async () => {
      try {
        const d = await enquiriesApi.getDailyCounts(30);
        if (alive) setDailyCounts(d);
      } catch (e) {
        console.error('daily counts failed', e);
      }
    })();
    return () => { alive = false; };
  }, [isAdminUser]);
  const gradeDatalistId = 'grade-options-master';
  const listIds = {
    product: 'product-options-master',
    sizeMm: 'size-options-master',
    lengthMm: 'length-options-master',
    heatTreatment: 'heat-treatment-options-master',
    tolerance: 'tolerance-options-master',
    chamfering: 'chamfering-options-master',
    unit: 'unit-options-master',
    ultrasonicTest: 'ut-options-master',
    surface: 'surface-options-master',
    incoterms: 'incoterms-options-master',
    priceOffer: 'price-offer-options-master',
    priceUnit: 'price-unit-options-master',
    paymentTerms: 'payment-terms-options-master',
    orderProgress: 'order-progress-options-master',
  };
  const getGradeOptionLabel = (grade) => {
    if (typeof grade === 'string') return grade.trim();
    const code = (grade?.code || '').trim();
    const name = (grade?.name || '').trim();
    if (code && name && code !== name) return `${code} - ${name}`;
    return code || name;
  };
  const gradeAllowSet = useMemo(() => new Set(GRADE_FALLBACK.map((g) => g.toLowerCase())), []);
  const isValidGradeOption = (value) => {
    const v = String(value || '').trim();
    if (!v) return false;
    if (gradeAllowSet.has(v.toLowerCase())) return true;
    return /[a-zA-Z]/.test(v) || v.includes('/');
  };

  const derivedGradeOptions = useMemo(() => {
    const collected = [];
    enquiries.forEach((row) => {
      const items = Array.isArray(row?.raw?.Items) ? row.raw.Items : [];
      items.forEach((item) => {
        const grade = String(item?.Grade || item?.grade || '').trim();
        if (grade) collected.push(grade);
      });
    });
    const deduped = [...new Set(collected.map((g) => g.toLowerCase()))];
    return deduped.map((g) => g.toUpperCase());
  }, [enquiries]);
  const availableGradeOptions = gradeOptions.length > 0
    ? gradeOptions
    : (derivedGradeOptions.length > 0 ? derivedGradeOptions : GRADE_FALLBACK);
  const filteredGradeOptions = availableGradeOptions.filter((g) =>
    isValidGradeOption(typeof g === 'string' ? g : getGradeOptionLabel(g))
  );
  const displayGradeOptions = filteredGradeOptions.length > 0 ? filteredGradeOptions : GRADE_FALLBACK;
  const toleranceClassOptions = useMemo(() => {
    const classes = toleranceOptions.map((t) => t.class_code).filter(Boolean);
    const deduped = [...new Set(classes.map((c) => c.toLowerCase()))];
    return deduped.length > 0 ? deduped : LOCAL_DROPDOWNS.tolerance;
  }, [toleranceOptions]);

  useEffect(() => {
    const fetchMasters = async () => {
      if (!showForm) return;
      setIsMastersLoading(true);
      setMastersError('');
      try {
        const [grades, tolerances] = await Promise.all([
          enquiriesApi.getGradeMasters(1000),
          enquiriesApi.getToleranceMasters(),
        ]);
        setGradeOptions(Array.isArray(grades) ? grades : []);
        setToleranceOptions(Array.isArray(tolerances) ? tolerances : []);
      } catch (error) {
        if ((error.message || '').toLowerCase().includes('not found')) {
          setMastersError('');
          setGradeOptions([]);
          setToleranceOptions([]);
        } else {
          setMastersError(error.message || 'Failed to load material masters');
        }
      } finally {
        setIsMastersLoading(false);
      }
    };

    fetchMasters();
  }, [showForm]);

  const resetForms = () => {
    setLocalForm(localDefault);
    setProductLines([{ ...defaultProductLine }]);
    setSaleType('LOCAL');
    draftStore.clear();
    setEditingEnquiry(null);
  };

  const closeForm = () => {
    setShowForm(false);
    resetForms();
  };

  const normalizeSaleType = (value) => {
    const raw = String(value || '').trim().toUpperCase();
    if (raw.startsWith('EXP')) return 'EXPORT';
    if (raw.startsWith('LOC')) return 'LOCAL';
    return 'LOCAL';
  };

  const normalizeRole = (value) =>
    String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[\s-]+/g, '_');

  const isSalesManagerRole = (role) => normalizeRole(role) === 'sales_manager';

  const buildFormFromEnquiry = async (enquiry) => {
    const sale = normalizeSaleType(enquiry?.sale_type || enquiry?.SaleType || enquiry?.BusinessType);
    const contactName = enquiry?.ContactPerson || enquiry?.ContactName || '';
    let contactEmail = enquiry?.ContactEmail || enquiry?.contact_email || '';
    let contactPhone = enquiry?.ContactPhone || enquiry?.contact_phone || '';
    const contactId = enquiry?.contact_id || enquiry?.ContactID;
    const accountId = enquiry?.account_id || enquiry?.AccountID;
    if ((!contactEmail || !contactPhone) && contactId && accountId) {
      try {
        const contacts = await contactsApi.getContactsByCompanyId(accountId);
        const matched = contacts.find(c => c.id === contactId || c.ID === contactId);
        if (matched) {
          contactEmail = contactEmail || matched.email_1 || matched.Email1 || matched.email || '';
          contactPhone = contactPhone || matched.phone_1 || matched.Phone1 || matched.phone || '';
        }
      } catch (e) {
        console.warn('Could not fetch contact details:', e);
      }
    }

    setSaleType(sale);
    setLocalForm({
      ...localDefault,
      saleType: sale,
      enquiryDate: enquiry?.EnquiryDate || enquiry?.enquiry_date || localDefault.enquiryDate,
      offerNo: enquiry?.offer_no || enquiry?.OfferNo || '',
      validityDays: enquiry?.validity || '',
      followupDate: enquiry?.next_followup_date || enquiry?.NextFollowupDate || '',
      customerName: enquiry?.AccountName || '',
      contactPerson: contactName,
      email: contactEmail,
      phone: contactPhone,
      salesperson: enquiry?.OwnerName || enquiry?.OwnerECode || getCurrentUserIdentity().name || '',
      kamEcode: enquiry?.kam_ecode || enquiry?.KAMECode || '',
      leadSource: enquiry?.lead_source || enquiry?.source || enquiry?.Source || localDefault.leadSource,
      paymentTerms: enquiry?.payment_terms || '',
      packing: enquiry?.packing || '',
      freight: enquiry?.freight || '',
      preparedBy: enquiry?.prepared_by || '',
      deliveryTerms: enquiry?.delivery_terms || '',
      port: enquiry?.port || '',
      specialNotes: enquiry?.tech_notes || enquiry?.TechNotes || enquiry?.notes || enquiry?.Notes || '',
      approvedByEcode: enquiry?.approved_by_ecode || enquiry?.ApprovedByEcode || '',
      deliveryNote: enquiry?.delivery_note || enquiry?.DeliveryNote || '',
      city: enquiry?.city || '',
      state: enquiry?.state || '',
    });

    const items = Array.isArray(enquiry?.Items) && enquiry.Items.length > 0
      ? enquiry.Items
      : Array.isArray(enquiry?.items)
        ? enquiry.items
        : [];

    setProductLines(
      items.length
        ? items.map((item) => ({
            product: item.Product || item.product || '',
            shape: item.Shape || item.shape || '',
            grade: item.Grade || item.grade || '',
            specification: item.Specification || item.specification || '',
            standard: item.Standard || item.standard || '',
            sizeMm: item.Dia || item.size_mm || '',
            lengthMm: item.Length || item.length_mm || '',
            heatTreatment: item.HeatTreatment || item.heat_treatment || '',
            tolerance: item.Tolerance || item.tolerance || '',
            chamfering: item.Chamfering || item.chamfering || '',
            qty: item.Qty || item.qty || '',
            unit: item.UOM || item.uom || '',
            ultrasonicTest: item.UT || item.ultrasonic_test || '',
            surface: item.Surface || item.surface || '',
            incoterms: item.Incoterms || item.incoterms || '',
            priceOffer: item.PriceOffer || item.price_offer || '',
            priceUnit: item.PriceUom || item.price_uom || '',
          }))
        : [{ ...defaultProductLine }]
    );
  };

  const handleCloseEnquiry = async () => {
    if (!selectedEnquiry?.id || !closingReason) return;
    setClosingBusy(true);
    try {
      await enquiriesApi.closeEnquiry(selectedEnquiry.id, closingReason);
      setShowClosePicker(false); setClosingReason('');
      setSelectedEnquiry({ ...selectedEnquiry, Status: 'CLOSED', DerivedStatus: 'Closed', close_reason: closingReason });
      fetchEnquiries();
    } catch (e) { console.error('close enquiry', e); }
    finally { setClosingBusy(false); }
  };

  const handleReopenEnquiry = async () => {
    if (!selectedEnquiry?.id) return;
    setClosingBusy(true);
    try {
      await enquiriesApi.reopenEnquiry(selectedEnquiry.id);
      setSelectedEnquiry({ ...selectedEnquiry, Status: 'NEW', DerivedStatus: 'Open', close_reason: null });
      fetchEnquiries();
    } catch (e) { console.error('reopen', e); }
    finally { setClosingBusy(false); }
  };

  const openEditEnquiry = async (enquiry) => {
    let fullEnquiry = enquiry;
    try {
      fullEnquiry = await enquiriesApi.getEnquiry(enquiry.id || enquiry.ID);
      setEditingEnquiry(fullEnquiry);
    } catch {
      setEditingEnquiry(enquiry);
    }
    await buildFormFromEnquiry(fullEnquiry);
    setFieldErrors({});
    setSaveError('');
    setSaveSuccess('');
    setShowForm(true);
    setSelectedEnquiry(null);
  };

  const duplicateEnquiry = async (enquiry) => {
    let fullEnquiry = enquiry;
    try {
      fullEnquiry = await enquiriesApi.getEnquiry(enquiry.id || enquiry.ID);
    } catch {}
    // Build form from existing enquiry
    await buildFormFromEnquiry(fullEnquiry);
    // Override: clear editing state so it creates NEW, reset dates
    setEditingEnquiry(null);
    setLocalForm(prev => ({
      ...prev,
      enquiryDate: new Date().toISOString().split('T')[0],
      followupDate: '',
      offerNo: '',
    }));
    setFieldErrors({});
    setSaveError('');
    setSaveSuccess('');
    setShowForm(true);
    setSelectedEnquiry(null);
  };

  const mapEnquiryToRow = (enquiry) => {
    const firstItem = enquiry?.Items?.[0] || {};
    const grade = firstItem.Grade || firstItem.grade || enquiry.Grade || enquiry.grade || "-";
    const dia = firstItem.Dia || firstItem.size_mm || enquiry.Dia || enquiry.dia || "-";
    const qty = firstItem.Qty || firstItem.qty || enquiry.Qty || enquiry.qty || "-";
    return {
      id: enquiry.id,
      saleType: enquiry.BusinessType || '-',
      company: enquiry.AccountName || '-',
      spec: `${grade} / ${dia} / ${qty}`,
      status: enquiry.DerivedStatus || enquiry.Status || 'NEW',
      pipelineStage: enquiry.PipelineStage || enquiry.pipeline_stage || null,
      owner: enquiry.OwnerName || enquiry.OwnerECode || '-',
      followup: enquiry.NextFollowupDate || '-',
      enquiryDate: enquiry.EnquiryDate || enquiry.enquiry_date || null,
      ageDays: (() => {
        const d = enquiry.EnquiryDate || enquiry.enquiry_date;
        if (!d) return null;
        const diff = Math.floor((Date.now() - new Date(d).getTime()) / 86400000);
        return Number.isFinite(diff) ? diff : null;
      })(),
      pipelineSynced: true,
      raw: enquiry,
    };
  };

  const fetchEnquiries = useCallback(async () => {
    setIsTableLoading(true);
    try {
      const stageActive = stageFilter && stageFilter !== 'ALL';
      const data = await enquiriesApi.getAllEnquiries({
        search: searchDebounced.trim() || undefined,
        limit: stageActive ? 5000 : SERVER_PAGE_SIZE,
        offset: stageActive ? 0 : (serverPage - 1) * SERVER_PAGE_SIZE,
        month: monthFilter || undefined,
        from_date: fromDate || undefined,
        to_date: toDate || undefined,
      });
      setEnquiries((Array.isArray(data) ? data : []).map(mapEnquiryToRow));
    } catch (error) {
      console.error('Error loading enquiries:', error);
    } finally {
      setIsTableLoading(false);
    }
  }, [searchDebounced, serverPage, monthFilter, fromDate, toDate, stageFilter]);

  const fetchEnquirySummary = useCallback(async () => {
    try {
      const summary = await enquiriesApi.getEnquirySummary(monthFilter || undefined, fromDate || undefined, toDate || undefined);
      if (typeof summary?.total_all === "number") {
        setTotalEnquiriesAll(summary.total_all);
      }
    } catch (error) {
      console.error("Error loading enquiry summary:", error);
    }
  }, [monthFilter, fromDate, toDate]);

  useEffect(() => {
    const handle = setTimeout(() => {
      setSearchDebounced(searchTerm);
    }, 350);
    return () => clearTimeout(handle);
  }, [searchTerm]);

  useEffect(() => {
    fetchEnquiries();
    fetchEnquirySummary();
  }, [fetchEnquiries, fetchEnquirySummary]);

  useEffect(() => {
    if (!enquiryIdFromQuery) return;
    enquiriesApi.getEnquiry(enquiryIdFromQuery).then(full => {
      if (editFromQuery) {
        openEditEnquiry(full);
      } else {
        setSelectedEnquiry(full);
      }
    }).catch(() => {
      const match = enquiries.find((row) => String(row.id) === String(enquiryIdFromQuery));
      if (match) {
        if (editFromQuery) openEditEnquiry(match.raw);
        else setSelectedEnquiry(match.raw);
      }
    });
  }, [enquiryIdFromQuery, editFromQuery]);

  const getCurrentUserIdentity = () => {
    const currentUser = userManager.getUser();
    return {
      ecode: currentUser?.ECode || currentUser?.ecode || '',
      name: currentUser?.Name || currentUser?.name || '',
    };
  };

  const sanitizeEmail = (value, companyName = 'contact') => {
    const input = (value || '').trim().toLowerCase();
    if (input.includes('@')) return input;
    const safe = companyName.toLowerCase().replace(/[^a-z0-9]+/g, '').slice(0, 16) || 'contact';
    return `${safe}@alokingots.local`;
  };

  const toIsoDate = (value) => {
    const text = (value || '').trim();
    if (!text) return null;
    const date = new Date(text);
    if (Number.isNaN(date.getTime())) return null;
    return date.toISOString().split('T')[0];
  };

  const parsePositiveNumber = (value) => {
    const numeric = Number.parseFloat(String(value || '').replace(/,/g, '').trim());
    return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
  };

  const normalizeLengthValue = (value) => {
    const raw = String(value || '').trim();
    if (!raw) return '';
    return raw.replace(/\s*-\s*/g, '-').replace(/\s+/g, ' ');
  };

  const isValidLengthValue = (value) => {
    const raw = normalizeLengthValue(value);
    if (!raw) return true;
    const rangeMatch = raw.match(/^(\d+)-(\d+)$/);
    if (rangeMatch) {
      const start = parsePositiveNumber(rangeMatch[1]);
      const end = parsePositiveNumber(rangeMatch[2]);
      if (!start || !end || start > end) return false;
      if (end > 13000) return false;
      return true;
    }
    const single = parsePositiveNumber(raw);
    if (!single) return false;
    if (single > 13000) return false;
    return true;
  };

  const isValidSizeValue = (value) => {
    const raw = String(value || '').trim();
    if (!raw) return false;
    if (parsePositiveNumber(raw)) return true;

    const normalized = raw.replace(/\s+/g, '').toUpperCase();
    const dimensionMatch = normalized.match(/^(\d+(?:\.\d+)?)X(\d+(?:\.\d+)?)$/);
    if (!dimensionMatch) return false;

    const first = parsePositiveNumber(dimensionMatch[1]);
    const second = parsePositiveNumber(dimensionMatch[2]);
    return Boolean(first && second);
  };

  const calculateDealValue = (line, currentSaleType = saleType) => {
    const qty = parsePositiveNumber(line?.qty);
    const price = parsePositiveNumber(line?.priceOffer);
    if (!qty || !price) return price || null;

    const qtyUnit = String(line?.unit || '').trim().toLowerCase();
    const priceUnit = String(line?.priceUnit || '').trim().toLowerCase();

    let normalizedQty = qty;
    if (qtyUnit === 'tons' || qtyUnit === 'ton' || qtyUnit === 'tonnes' || qtyUnit === 'tonne') {
      normalizedQty = qty * 1000;
    }

    let baseValue = qty * price;
    if (priceUnit.includes('/kg')) {
      baseValue = normalizedQty * price;
    } else if (priceUnit.includes('/ton') || priceUnit.includes('/tons') || priceUnit.includes('/tonne')) {
      baseValue = qty * price;
    } else if (qtyUnit === 'kg') {
      baseValue = qty * price;
    } else if (qtyUnit === 'tons' || qtyUnit === 'ton' || qtyUnit === 'tonnes' || qtyUnit === 'tonne') {
      baseValue = qty * price;
    }

    // if ((currentSaleType || '').toUpperCase() === 'LOCAL') {
    //   return baseValue * 1.18;
    // }
    return baseValue;
  };

  const normalizeTextValue = (value) => String(value || '').replace(/[ \t]+/g, ' ').trim();

  const normalizeNumericValue = (value, { allowDecimal = false } = {}) => {
    let raw = String(value || '');
    if (!allowDecimal) {
      return raw.replace(/[^0-9]/g, '');
    }
    // Support decimal comma keyboards by converting first comma to dot.
    raw = raw.replace(',', '.');
    const cleaned = raw.replace(/[^0-9.]/g, '');
    const [whole, ...rest] = cleaned.split('.');
    return rest.length ? `${whole}.${rest.join('')}` : whole;
  };

  const getOrCreateAccount = async ({ companyName, division, location, notes }) => {
    const target = (companyName || '').trim().toLowerCase();
    const searchResults = await accountsApi.searchAccounts(companyName);
    const existing = searchResults.find((acc) => (acc.Name || '').trim().toLowerCase() === target);
    if (existing) return existing.id;
    const _payload = { Name: companyName, Division: division, Location: location || null, Notes: notes };
    try {
      const created = await accountsApi.createAccount(_payload);
      return created.id;
    } catch (err) {
      if (err && err.isDuplicate) {
        return new Promise((resolve, reject) => {
          setDupModal({
            matches: err.matches || [],
            companyName,
            onUse: (id) => { setDupModal(null); resolve(id); },
            onForce: async () => {
              setDupModal(null);
              try { const forced = await accountsApi.createAccount({ ..._payload, force_create: true }); resolve(forced.id); }
              catch (e) { reject(e); }
            },
            onCancel: () => { setDupModal(null); reject(new Error('cancelled')); },
          });
        });
      }
      throw err;
    }
  }

  const getOrCreateContact = async ({ accountId, contactName, email, phone, notes }) => {
    const accountContacts = await contactsApi.getContactsByAccountId(accountId);
    const target = (contactName || '').trim().toLowerCase();
    const targetEmail = (email || '').trim().toLowerCase();
    // Match by email first (backend's unique key), then by name. Reuse if found
    // so a returning contact doesn't trigger a 409 duplicate-email conflict.
    const existing = accountContacts.find((c) => {
      const cEmail = (c.Email1 || c.email_1 || c.Email || '').trim().toLowerCase();
      if (targetEmail && cEmail && cEmail === targetEmail) return true;
      return (c.Name || '').trim().toLowerCase() === target;
    });
    if (existing) return existing.id;
    try {
      const created = await contactsApi.createContact({
        Name: contactName || 'Primary Contact',
        AccountID: accountId,
        Designation: 'Procurement',
        Email1: sanitizeEmail(email, contactName),
        Phone1: (phone || '').trim() || null,
        Notes: notes || null
      });
      return created.id;
    } catch (err) {
      if (err && (err.status === 409 || (err.message && (err.message.includes('409') || err.message.includes('already exists'))))) {
        const fallback = await contactsApi.createContact({
          Name: contactName || 'Primary Contact',
          AccountID: accountId,
          Designation: 'Procurement',
          Email1: null,
          Phone1: (phone || '').trim() || null,
          Notes: notes || null
        });
        return fallback.id;
      }
      throw err;
    }
  };

  const buildEnquiryItemsPayload = () =>
    productLines.map((line) => ({
      product: normalizeTextValue(line.product) || null,
      grade: normalizeTextValue(line.grade) || null,
      shape: normalizeTextValue(line.shape) || null,
      size_mm: normalizeTextValue(line.sizeMm) || null,
      length_mm: normalizeLengthValue(line.lengthMm) || null,
      heat_treatment: normalizeTextValue(line.heatTreatment) || null,
      tolerance: normalizeTextValue(line.tolerance) || null,
      chamfering: normalizeTextValue(line.chamfering) || null,
      qty: normalizeTextValue(line.qty) || null,
      uom: normalizeTextValue(line.unit) || null,
      ultrasonic_test: normalizeTextValue(line.ultrasonicTest) || null,
      surface: normalizeTextValue(line.surface) || null,
      incoterms: normalizeTextValue(line.incoterms) || null,
      price_offer: normalizeTextValue(line.priceOffer) || null,
      price_uom: normalizeTextValue(line.priceUnit) || null,
      specification: normalizeTextValue(line.specification) || null,
      notes: null,
    }));

  const createEnquiryRecord = async ({ accountId, contactId }) => {
    const currentUser = getCurrentUserIdentity();
    const f = localForm;

    const created = await enquiriesApi.createEnquiry({
      account_id: accountId,
      contact_id: contactId,
      owner_ecode: currentUser.ecode,
      sale_type: saleType,
      offer_no: normalizeTextValue(f.offerNo) || null,
      enquiry_date: new Date().toISOString().split('T')[0],
      status: 'NEW',
      source: normalizeTextValue(f.leadSource) || 'Inbound Inquiry',
      lead_source: normalizeTextValue(f.leadSource) || 'Inbound Inquiry',
      business_type: saleType === 'LOCAL' ? 'Local' : 'Export',
      kam_ecode: normalizeTextValue(f.kamEcode) || null,
      tech_notes: normalizeTextValue(f.specialNotes) || null,
      payment_terms: normalizeTextValue(f.paymentTerms) || null,
      delivery_terms: normalizeTextValue(f.deliveryTerms) || null,
      packing: normalizeTextValue(f.packing) || null,
      freight: normalizeTextValue(f.freight) || null,
      prepared_by: normalizeTextValue(f.preparedBy) || null,
      validity: normalizeTextValue(f.validityDays) || null,
      port: saleType === 'EXPORT' ? normalizeTextValue(f.port) || null : null,
      tech_notes: normalizeTextValue(f.specialNotes) || null,
      items: buildEnquiryItemsPayload(),
    });

    return created?.id;
  };

  const createDealFromEnquiry = async () => {
    const currentUser = getCurrentUserIdentity();
    const userECode = currentUser.ecode;

    if (!userECode) {
      throw new Error('User session not found. Please login again.');
    }

    if (saleType === 'LOCAL') {
      const f = localForm;
      const primaryLine = productLines[0] || defaultProductLine;
      const companyName = (f.customerName || '').trim();
      if (!companyName) throw new Error('Customer Name is required to sync with Sales Pipeline.');

      const accountId = await getOrCreateAccount({
        companyName,
        division: 'LOCAL',
        location: [f.city, f.state].filter(Boolean).join(', '),
        notes: 'Auto-created from Local Enquiry form'
      });

      const contactId = await getOrCreateContact({
        accountId,
        contactName: (f.contactPerson || '').trim() || companyName,
        email: f.email,
        phone: f.phone,
      notes: 'Auto-created from Local Enquiry form'
      });

      const enquiryId = await createEnquiryRecord({ accountId, contactId });

      const notes = [
        `Sale Type: Local`,
        `Offer No: ${f.offerNo || '-'}`,
        `Product Lines:`,
        ...productLines.map((line, idx) =>
          `${idx + 1}. Product:${line.product || '-'} | Shape:${line.shape || '-'} | Grade:${line.grade || '-'} | Size:${line.sizeMm || '-'}mm | Qty:${line.qty || '-'} ${line.unit || ''} | Length:${normalizeLengthValue(line.lengthMm) || '-'} | Spec:${line.specification || '-'} | UT:${line.ultrasonicTest || '-'} | Surface:${line.surface || '-'} | Incoterms:${line.incoterms || '-'} | Price:${line.priceOffer || '-'} ${line.priceUnit || ''}`.trim()
        ),
        `Commercial: Payment Terms=${f.paymentTerms || '-'}, Packing=${f.packing || '-'}, Delivery Terms=${f.deliveryTerms || '-'}, Validity=${f.validityDays || '-'} Days`,
        normalizeTextValue(f.specialNotes) ? `Special Notes: ${normalizeTextValue(f.specialNotes)}` : '',
      ].filter(Boolean).join('\n');

      await dealsApi.createDeal({
        EnquiryID: enquiryId,
        AccountID: accountId,
        ContactID: contactId,
        SalespersonECode: userECode,
        KAMECode: normalizeTextValue(f.kamEcode) || null,
        SaleType: 'LOCAL',
        Division: 'LOCAL',
        ServiceType: `RFQ - ${primaryLine.product || primaryLine.shape || 'General'}`,
        DealValue: calculateDealValue(primaryLine, 'LOCAL'),
        ExpectedClosureDate: toIsoDate(f.followupDate),
        LeadGeneratedBy: userECode,
        LeadSource: normalizeTextValue(f.leadSource) || 'Inbound Inquiry',
        Stage: 'NEW',
        Notes: notes
      });
      return;
    }

    const f = localForm;
    const primaryLine = productLines[0] || defaultProductLine;
    const companyName = (f.customerName || '').trim();
    if (!companyName) throw new Error('Customer Name is required to sync with Sales Pipeline.');

    const accountId = await getOrCreateAccount({
      companyName,
      division: 'EXPORT',
      location: [f.city, f.state].filter(Boolean).join(', '),
      notes: 'Auto-created from Export Enquiry form'
    });

    const contactId = await getOrCreateContact({
      accountId,
      contactName: (f.contactPerson || '').trim() || companyName,
      email: f.email,
      phone: f.phone,
      notes: 'Auto-created from Export Enquiry form'
    });

    const enquiryId = await createEnquiryRecord({ accountId, contactId });

    const notes = [
      `Sale Type: Export`,
      `Offer No: ${f.offerNo || '-'}`,
      `Product Lines:`,
      ...productLines.map((line, idx) =>
        `${idx + 1}. Product:${line.product || '-'} | Shape:${line.shape || '-'} | Grade:${line.grade || '-'} | Size:${line.sizeMm || '-'}mm | Qty:${line.qty || '-'} ${line.unit || ''} | Length:${normalizeLengthValue(line.lengthMm) || '-'} | Spec:${line.specification || '-'} | UT:${line.ultrasonicTest || '-'} | Surface:${line.surface || '-'} | Incoterms:${line.incoterms || '-'} | Price:${line.priceOffer || '-'} ${line.priceUnit || ''}`.trim()
      ),
      `Commercial: Payment Terms=${f.paymentTerms || '-'}, Packing=${f.packing || '-'}, Delivery Terms=${f.deliveryTerms || '-'}, Validity=${f.validityDays || '-'} Days, Port=${f.port || '-'}`,
      normalizeTextValue(f.specialNotes) ? `Special Notes: ${normalizeTextValue(f.specialNotes)}` : '',
    ].filter(Boolean).join('\n');

    await dealsApi.createDeal({
      EnquiryID: enquiryId,
      AccountID: accountId,
      ContactID: contactId,
      SalespersonECode: userECode,
      KAMECode: normalizeTextValue(f.kamEcode) || null,
      SaleType: 'EXPORT',
      Division: 'EXPORT',
      ServiceType: `RFQ - ${primaryLine.product || primaryLine.shape || 'General'}`,
      DealValue: calculateDealValue(primaryLine, 'EXPORT'),
      ExpectedClosureDate: toIsoDate(f.followupDate),
      LeadGeneratedBy: userECode,
      LeadSource: normalizeTextValue(f.leadSource) || 'Inbound Inquiry',
      Stage: 'NEW',
      Notes: notes
    });
  };

  const validateEnquiryForm = () => {
    const errors = {};
    const primaryLine = productLines[0] || defaultProductLine;
    if (!['LOCAL', 'EXPORT'].includes(saleType)) errors.saleType = 'Choose Local or Export.';
    if (!normalizeTextValue(localForm.customerName)) errors.customerName = 'Customer Name is required. Example: Silver Tubes';
    if (!normalizeTextValue(localForm.contactPerson)) errors.contactPerson = 'Contact Person is required. Example: Ajay Mehta';
    if (!localForm.followupDate) errors.followupDate = 'Follow-up Date is required. Example: 15/04/2026';
    if (!normalizeTextValue(localForm.paymentTerms)) errors.paymentTerms = 'Payment Terms is required. Example: 30% advance, balance 70% in 30 days';
    if (!String(localForm.validityDays || '').trim()) {
      errors.validityDays = 'Validity is required. Example: 30 Days';
    }
    if (saleType === 'EXPORT' && !normalizeTextValue(localForm.port)) errors.port = 'Port is required for Export enquiries.';
    if (!normalizeTextValue(localForm.email)) {
      errors.email = 'Email is required. Example: buyer@company.com';
    } else if (!EMAIL_REGEX.test(localForm.email.trim())) {
      errors.email = 'Email format is invalid.';
    }
    if (!normalizeTextValue(localForm.phone)) {
      errors.phone = 'Phone is required. Example: +91 98765 43210';
    } else if (!PHONE_REGEX.test(localForm.phone.trim())) {
      errors.phone = 'Phone should contain only digits and basic separators.';
    }
    if (!normalizeTextValue(primaryLine.product)) errors.product = 'Product is required.';
    if (!normalizeTextValue(primaryLine.grade) || normalizeTextValue(primaryLine.grade).length < 2) errors.grade = 'Grade must be at least 2 characters. Example: 304';
    if (normalizeTextValue(primaryLine.specification) && normalizeTextValue(primaryLine.specification).length < 2) {
      errors.specification = 'Specification must be at least 2 characters.';
    }
    if (normalizeTextValue(primaryLine.standard) && normalizeTextValue(primaryLine.standard).length < 2) {
      errors.standard = 'Standard must be at least 2 characters.';
    }
    if (!String(primaryLine.sizeMm).trim()) {
      errors.sizeMm = 'Size is required. Example: 20 or 200X200';
    } else if (!isValidSizeValue(primaryLine.sizeMm)) {
      errors.sizeMm = 'Size must be a positive number or dimension like 200X200.';
    }
    if (primaryLine.lengthMm && !isValidLengthValue(primaryLine.lengthMm)) {
      errors.lengthMm = 'Length must be a positive number or range like 4000 - 4100.';
    }
    if (!normalizeTextValue(primaryLine.heatTreatment)) errors.heatTreatment = 'Heat Treatment is required. Example: Ann / QT';
    if (!normalizeTextValue(primaryLine.tolerance)) errors.tolerance = 'Tolerance is required. Example: h9 / k12';
    if (String(primaryLine.qty).trim() && !parsePositiveNumber(primaryLine.qty)) {
      errors.qty = 'Qty must be a positive number. Example: 500 or 2.5';
    }
    if (String(primaryLine.priceOffer).trim() && !parsePositiveNumber(primaryLine.priceOffer)) {
      errors.priceOffer = 'Price must be a positive number. Example: 120';
    }
    setFieldErrors(errors);
    return {
      isValid: Object.keys(errors).length === 0,
      firstError: Object.values(errors)[0] || ''
    };
  };

  const handleSaveEnquiry = async () => {
    setIsSaving(true);
    setSaveError('');
    setSaveSuccess('');

    const validation = validateEnquiryForm();
    if (!validation.isValid) {
      setSaveError(validation.firstError || 'Please complete required fields.');
      setIsSaving(false);
      return;
    }

    try {
      if (editingEnquiry) {
        const currentUser = getCurrentUserIdentity();
        const accountId = editingEnquiry.AccountID || editingEnquiry.account_id;
        let contactId = editingEnquiry.ContactID || editingEnquiry.contact_id;

        if (accountId) {
          await accountsApi.updateAccount(accountId, {
            Name: normalizeTextValue(localForm.customerName) || editingEnquiry.AccountName || '-',
            Division: saleType,
            Location: [localForm.city, localForm.state].filter(Boolean).join(', ') || null,
          });
        }

        if (contactId) {
          await contactsApi.updateContact(contactId, {
            Name: normalizeTextValue(localForm.contactPerson) || editingEnquiry.ContactName || 'Primary Contact',
            Email1: sanitizeEmail(localForm.email, localForm.customerName),
            Phone1: (localForm.phone || '').trim() || null,
          });
        } else if (accountId && normalizeTextValue(localForm.contactPerson)) {
          contactId = await getOrCreateContact({
            accountId,
            contactName: normalizeTextValue(localForm.contactPerson),
            email: localForm.email,
            phone: localForm.phone,
          });
        }


        await enquiriesApi.updateEnquiry(editingEnquiry.id, {
          account_id: accountId,
          contact_id: contactId,
          owner_ecode: editingEnquiry.OwnerECode || currentUser.ecode,
          sale_type: saleType,
          offer_no: normalizeTextValue(localForm.offerNo) || null,
          enquiry_date: localForm.enquiryDate || editingEnquiry.EnquiryDate || new Date().toISOString().split('T')[0],
          status: editingEnquiry.Status || 'NEW',
          source: normalizeTextValue(localForm.leadSource) || null,
          lead_source: normalizeTextValue(localForm.leadSource) || null,
          business_type: saleType === 'LOCAL' ? 'Local' : 'Export',
          kam_ecode: normalizeTextValue(localForm.kamEcode) || null,
          tech_notes: normalizeTextValue(localForm.specialNotes) || null,
          approved_by_ecode: normalizeTextValue(localForm.approvedByEcode) || null,
          delivery_note: normalizeTextValue(localForm.deliveryNote) || null,
          next_followup_date: toIsoDate(localForm.followupDate),
          enquiry_date: localForm.enquiryDate || new Date().toISOString().split('T')[0],
          validity: normalizeTextValue(localForm.validityDays) || null,
          payment_terms: normalizeTextValue(localForm.paymentTerms) || null,
          packing: normalizeTextValue(localForm.packing) || null,
          freight: normalizeTextValue(localForm.freight) || null,
          prepared_by: normalizeTextValue(localForm.preparedBy) || null,
          delivery_terms: normalizeTextValue(localForm.deliveryTerms) || null,
          port: saleType === 'EXPORT' ? normalizeTextValue(localForm.port) || null : null,
          items: buildEnquiryItemsPayload(),
        });

        await fetchEnquiries();
        setSaveSuccess('Enquiry updated.');
        closeForm();
        setSelectedEnquiry(null);
      } else {
        await createDealFromEnquiry();
        await fetchEnquiries();
        setSaveSuccess('Enquiry saved and synced to Sales Pipeline.');
        closeForm();
      }
    } catch (error) {
      setSaveError(error.message || 'Failed to save enquiry.');
    } finally {
      setIsSaving(false);
    }
  };

  const generateOfferNo = (type = 'LOCAL') => {
    const now = new Date();
    const yy = String(now.getFullYear()).slice(-2);
    const nextYy = String(now.getFullYear() + 1).slice(-2);
    const token = String(Date.now()).slice(-4);
    const label = type === 'EXPORT' ? 'EXPORT' : 'LOCAL';
    return `OFF-${token}/${label}/${yy}-${nextYy}`;
  };

  useEffect(() => {
    if (showForm) {
      setFieldErrors({});
      if (!editingEnquiry) {
        setLocalForm((prev) => ({
          ...prev,
          offerNo: generateOfferNo(saleType),
          salesperson: prev.salesperson || getCurrentUserIdentity().name || ''
        }));
      }
    }
  }, [showForm, saleType, editingEnquiry]);

  useEffect(() => {
    if (!showForm || !editingEnquiry) return;
    const accountId = editingEnquiry.AccountID || editingEnquiry.account_id;
    const contactId = editingEnquiry.ContactID || editingEnquiry.contact_id;
    if (!accountId || !contactId) return;
    let isActive = true;
    contactsApi
      .getContactsByAccountId(accountId)
      .then((list) => {
        if (!isActive) return;
        const match = (Array.isArray(list) ? list : []).find(
          (c) => String(c.ID || c.id) === String(contactId)
        );
        if (!match) return;
        setLocalForm((prev) => ({
          ...prev,
          contactPerson: match.Name || prev.contactPerson,
          email: match.Email1 || prev.email,
          phone: match.Phone1 || prev.phone,
        }));
      })
      .catch(() => {});
    return () => {
      isActive = false;
    };
  }, [showForm, editingEnquiry]);

  useEffect(() => {
    if (!showForm) return;
    let isActive = true;

    // Auto-populate SM based on SC mapping
    const loadSMForSC = async () => {
      try {
        const response = await fetch('/api/v1/users/sm-for-sc', {
          headers: { 'Authorization': 'Bearer ' + localStorage.getItem('access_token') }
        });
        const data = await response.json();
        if (data.sm_ecode) {
          setLocalForm((p) => ({ ...p, kamEcode: p.kamEcode || data.sm_ecode }));
        }
      } catch (e) { console.error('SM lookup failed', e); }
    };
    loadSMForSC();
    const loadSalesManagers = async () => {
      try {
        setIsSalesManagersLoading(true);
        setSalesManagersError('');
        const users = await usersApi.getAllUsers();
        const managers = (Array.isArray(users) ? users : []).filter((user) =>
          isSalesManagerRole(user?.Role || user?.role)
        );
        if (isActive) {
          setSalesManagers(managers);
          setScUsers(
            (Array.isArray(users) ? users : []).filter((u) => {
              const r = String(u?.Role || u?.role || '').toLowerCase().replace(/ /g, '_');
              return r === 'sales_coordinator' || r === 'local_admin' || r === 'admin';
            })
          );
        }
      } catch (error) {
        console.error('Error loading sales managers:', error);
        if (isActive) {
          setSalesManagers([]);
          setSalesManagersError(error.message || 'Failed to load sales managers.');
        }
      } finally {
        if (isActive) {
          setIsSalesManagersLoading(false);
        }
      }
    };

    loadSalesManagers();
    return () => {
      isActive = false;
    };
  }, [showForm]);

  const getInputClass = (field) =>
    `border rounded-lg px-3 py-2 text-sm w-full ${
      fieldErrors[field] ? 'border-red-400 bg-red-50' : 'border-gray-300'
    }`;

  const FieldLabel = ({ children, required = false }) => (
    <label className="mb-1 block text-xs font-medium text-gray-600">
      {children} {required && <span className="text-red-500">*</span>}
    </label>
  );

  const updateProductLine = (index, key, value) => {
    setProductLines((prev) =>
      prev.map((line, i) => {
        if (i !== index) return line;
        const normalizedValue =
          key === 'lengthMm'
            ? normalizeLengthValue(value)
            : key === 'qty'
                ? normalizeNumericValue(value, { allowDecimal: true })
                : key === 'priceOffer'
                  ? normalizeNumericValue(value, { allowDecimal: true })
                  : value;
        if (key === 'product') {
          return {
            ...line,
            product: normalizedValue,
            shape: getShapeFromProduct(normalizedValue),
          };
        }
        return { ...line, [key]: normalizedValue };
      })
    );
  };

  const addProductLine = () => {
    setProductLines((prev) => [...prev, { ...defaultProductLine }]);
  };

  const duplicateProductLine = (index) => {
    setProductLines((prev) => {
      const line = prev[index] || defaultProductLine;
      const copy = { ...line };
      return [...prev.slice(0, index + 1), copy, ...prev.slice(index + 1)];
    });
  };

  const removeProductLine = (index) => {
    setProductLines((prev) => {
      if (prev.length <= 1) return [{ ...defaultProductLine }];
      return prev.filter((_, i) => i !== index);
    });
  };

  const normalizeHeader = (value) => String(value || '').trim().toLowerCase();

  const buildDefaultMapping = (headers) => {
    const pick = (candidates) =>
      headers.findIndex((h) => candidates.some((c) => normalizeHeader(h) === c)) !== -1
        ? headers.findIndex((h) => candidates.some((c) => normalizeHeader(h) === c))
        : headers.findIndex((h) => candidates.some((c) => normalizeHeader(h).includes(c)));

    return {
      material: pick(['section', 'product', 'item', 'description']),
      shape: pick(['shape', 'form', 'section shape']),
      grade: pick(['grade', 'material grade', 'steel grade', 'material']),
      size: pick(['size(mm)', 'size (mm)', 'size_mm', 'outside dia', 'diameter', 'od', 'dimension', 'dia', 'size']),
      length: pick(['length', 'len']),
      qty: pick(['qty(kg)', 'quantity', 'weight', 'total weight', 'kg', 'qty']),
      tolerance: pick(['tol', 'tolerance']),
      price: pick(['cif effective', 'unit price', 'price', 'rate', 'cif', 'eur/ton', 'usd/ton', 'inr/kg', 'effective']),
      unit: pick(['unit', 'uom', 'mt', '/mt', '/kg']),
      heat_treatment: pick(['condition', 'heat treatment', 'ht', 'heat']),
      surface: pick(['type', 'surface', 'finish']),
      specification: pick(['hardness', 'specification', 'spec']),
    };
  };

  const getHeaderRowIndex = (rows) => {
    if (!rows.length) return 0;
    const sampleSize = Math.min(rows.length, 5);
    let bestIndex = 0;
    let bestScore = -1;
    for (let i = 0; i < sampleSize; i += 1) {
      const row = rows[i] || [];
      const nonEmpty = row.filter((cell) => String(cell || '').trim()).length;
      const textCells = row.filter((cell) => typeof cell === 'string' && String(cell || '').trim()).length;
      const score = nonEmpty + textCells;
      if (score > bestScore) {
        bestScore = score;
        bestIndex = i;
      }
    }
    return bestIndex;
  };

  const applyHeaderSelection = (rows, headerRowIndex) => {
    const headerRow = rows[headerRowIndex] || [];
    const headers = headerRow.map((h) => String(h || '').trim());
    const bodyRows = rows.slice(headerRowIndex + 1).filter((r) => r.some((cell) => String(cell || '').trim()));
    const mapping = buildDefaultMapping(headers);
    return { headers, bodyRows, mapping };
  };

  const openImportDialog = () => {
    setImportState((prev) => ({ ...prev, open: true, error: '' }));
  };

  const closeImportDialog = () => {
    setImportState({
      open: false,
      headers: [],
      rows: [],
      rawRows: [],
      headerRowIndex: 0,
      file: null,
      mapping: {
        material: null,
        shape: null,
        grade: null,
        size: null,
        length: null,
        qty: null,
        tolerance: null,
        price: null,
        unit: null,
      },
      error: '',
      filename: '',
    });
  };

  const handleImportFile = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.xlsx') && !file.name.toLowerCase().endsWith('.xls')) {
      setImportState((prev) => ({ ...prev, error: 'Please select an Excel file (.xlsx or .xls).' }));
      return;
    }
    const data = await file.arrayBuffer();
    const workbook = XLSX.read(data, { type: 'array' });
    // Read ALL sheets and combine rows (supports multi-sheet RFQ files)
    const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
    const headerRows = XLSX.utils.sheet_to_json(firstSheet, { header: 1, defval: '', range: 0 }).slice(0, 1);
    let rows = [];
    workbook.SheetNames.forEach((sheetName) => {
      const sheet = workbook.Sheets[sheetName];
      const sheetRows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
      if (sheetRows.length > 1) rows = rows.concat(sheetRows.slice(1));
    });
    rows = [...headerRows, ...rows];
    if (!rows.length) {
      setImportState((prev) => ({ ...prev, error: 'No rows found in this file.' }));
      return;
    }
    // Auto-detect header row: first row with 2+ non-empty cells
    // This handles files with a blank top row (e.g. ready_stock.xlsx)
    let headerRowIndex = 0;
    for (let i = 0; i < Math.min(rows.length, 10); i++) {
      const nonEmpty = (rows[i] || []).filter((c) => String(c || '').trim()).length;
      if (nonEmpty >= 2) { headerRowIndex = i; break; }
    }
    const { headers, bodyRows, mapping } = applyHeaderSelection(rows, headerRowIndex);
    setImportState((prev) => ({
      ...prev,
      headers,
      rows: bodyRows,
      rawRows: rows,
      headerRowIndex,
      file,
      mapping,
      filename: file.name,
      error: '',
    }));
  };

  const applyImport = async () => {
    if (!importState.rows.length) return;
    const { mapping, file } = importState;
    if (!file) {
      setImportState((prev) => ({ ...prev, error: 'Please select a file.' }));
      return;
    }
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('mapping', JSON.stringify(mapping));
      const response = await enquiriesApi.importItems(form);
      const items = response?.items || [];
      const nextLines = items.map((row) => ({
        ...defaultProductLine,
        product: row.product || '',
        shape: row.shape || (row.product ? getShapeFromProduct(row.product) : ''),
        tolerance: row.tolerance || '',
        grade: row.grade || '',
        sizeMm: row.size_mm || '',
        lengthMm: row.length_mm || '',
        qty: row.qty || '',
        priceOffer: row.price_offer || '',
        priceUnit: row.price_uom || '',
        heatTreatment: row.heat_treatment || '',
        surface: row.surface || '',
        specification: row.specification || '',
      }));
      setProductLines(nextLines.length ? nextLines : [{ ...defaultProductLine }]);
      closeImportDialog();
    } catch (error) {
      const rawMessage = String(error?.message || '');
      const friendly = rawMessage.toLowerCase().includes('token')
        ? 'Session expired. Please login again and retry import.'
        : rawMessage || 'Import failed.';
      setImportState((prev) => ({ ...prev, error: friendly }));
    }
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Enquiries (RFQ)</h1>
            <p className="text-gray-600 mt-1">Local sales inquiry + export offer capture in one workflow.</p>
            <p className="text-xs text-gray-500 mt-1">New enquiry will auto-create account + contact if missing.</p>
          </div>
          <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => exportApi.downloadExcel('enquiries').catch((e) => alert('Export failed: ' + e.message))}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
          >
            Export to Excel
          </button>
          <button
            type="button"
            onClick={() => setShowForm(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
          >
            New Enquiry
          </button>
          </div>
        </div>
        {saveSuccess && (
          <div className="mb-4 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
            {saveSuccess}
          </div>
        )}
        {saveError && (
          <div className="mb-4 rounded-lg border border-orange-200 bg-orange-50 px-4 py-3 text-sm text-orange-800">
            {saveError}
          </div>
        )}

        <section className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-5 gap-4 mb-6">
          {kpiCards.map((card) => (
            <div key={card.label} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <p className="text-sm text-gray-600">{card.label}</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{card.value}</p>
            </div>
          ))}
        </section>

        <section className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Enquiry List</h2>
                <p className="text-sm text-gray-500">
                  Search company, grade, size, qty, owner, follow-up, contact, price, or incoterms.
                </p>
              </div>
              <div className="flex w-full flex-col gap-3 lg:w-auto lg:flex-row lg:items-center">
                <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-1">
                  <button
                    type="button"
                    onClick={() => setStageFilter('ALL')}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md ${
                      stageFilter === 'ALL' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    All
                  </button>
                  <button
                    type="button"
                    onClick={() => setStageFilter('PENDING_PRICE')}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md ${
                      stageFilter === 'PENDING_PRICE' ? 'bg-yellow-100 text-yellow-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Pending Price
                  </button>
                </div>
                <div>
                  <select
                    value={monthFilter}
                    onChange={(e) => setMonthFilter(e.target.value)}
                    className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                  >
                    <option value="">All months</option>
                    {monthOptions.map((m) => (
                      <option key={m.value} value={m.value}>{m.label}</option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center gap-1">
                  <input type="date" value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    className="rounded-lg border border-gray-300 px-2 py-2 text-sm" title="From date" />
                  <span className="text-gray-400 text-xs">to</span>
                  <input type="date" value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    className="rounded-lg border border-gray-300 px-2 py-2 text-sm" title="To date" />
                  {(fromDate || toDate) && (
                    <button type="button"
                      onClick={() => { setFromDate(''); setToDate(''); }}
                      className="text-xs text-gray-500 hover:text-gray-800 px-1">clear</button>
                  )}
                </div>
                <div className="w-full lg:w-80">
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search company, grade, size, SC name, SM name, status, source..."
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="md:hidden divide-y divide-gray-100">
            {isTableLoading && <div className="px-4 py-8 text-sm text-center text-gray-500">Loading enquiries...</div>}
              <div className="px-4 py-8 text-sm text-center text-gray-500">
                {searchTerm ? (<div className="flex flex-col items-center gap-3"><span>No enquiries match this search.</span><button type="button" onClick={() => setSearchTerm('')} className="inline-flex items-center rounded-md border border-blue-300 px-3 py-1 text-xs font-medium text-blue-700">Clear search</button></div>) : (<div className="flex flex-col items-center gap-2"><span>No enquiries yet.</span><button type="button" onClick={() => setShowForm(true)} className="inline-flex items-center rounded-md bg-blue-600 px-3 py-1 text-xs font-medium text-white">New Enquiry</button></div>)}
              </div>
            )}
            {pagedRows.map((row) => (
              <div key={row.id} className="px-4 py-3 bg-white hover:bg-gray-50 active:bg-gray-100">
                <div className="flex items-start justify-between mb-1">
                  <span className="text-sm font-semibold text-gray-900 flex-1 mr-2">{row.company}</span>
                  <span className={row.saleType === 'EXPORT' ? 'flex-shrink-0 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700' : 'flex-shrink-0 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-orange-100 text-orange-700'}>{row.saleType}</span>
                </div>
                <div className="text-xs text-gray-600 mb-1">{row.spec}</div>
                <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500 mb-2">
                  <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 font-medium text-gray-700">{row.status}</span>
                  <span>{row.owner}</span>
                  {row.followup && <span>📅 {row.followup}</span>}
                </div>
                <button type="button" onClick={async () => { try { const full = await enquiriesApi.getEnquiry(row.id); setSelectedEnquiry(full); } catch { setSelectedEnquiry(row.raw); } }} className="inline-flex items-center rounded-md border border-blue-300 px-3 py-1 text-xs font-medium text-blue-700 hover:bg-blue-50">View</button>
                  <button type="button" onClick={async () => { try { const full = await enquiriesApi.getEnquiry(row.id); await duplicateEnquiry(full); } catch { await duplicateEnquiry(row.raw); } }} className="inline-flex items-center rounded-md border border-green-300 px-3 py-1 text-xs font-medium text-green-700 hover:bg-green-50 ml-1">Duplicate</button>
              </div>
            ))}
          </div>
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sale Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Company</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Grade / Dia / Qty</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Owner</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Enquiry Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {isTableLoading && (<tr><td colSpan={7} className="px-6 py-10 text-sm text-center text-gray-500">Loading enquiries...</td></tr>)}
                {pagedRows.map((row) => (<tr key={row.id}><td className="px-6 py-3 text-sm text-gray-700">{row.saleType}</td><td className="px-6 py-3 text-sm text-gray-900 font-medium">{row.company}</td><td className="px-6 py-3 text-sm text-gray-700">{row.spec}</td><td className="px-6 py-3 text-sm text-gray-700">{row.status}</td><td className="px-6 py-3 text-sm text-gray-700">{row.owner}</td><td className="px-6 py-3 text-sm text-gray-700">{row.enquiryDate ? (<div><div>{new Date(row.enquiryDate).toLocaleDateString('en-GB')}</div>{row.ageDays !== null && (<div className={`text-xs ${row.ageDays > 30 ? 'text-red-600' : 'text-gray-400'}`}>{row.ageDays} days</div>)}</div>) : '-'}</td><td className="px-6 py-3 text-sm"><button type="button" onClick={async () => { try { const full = await enquiriesApi.getEnquiry(row.id); setSelectedEnquiry(full); } catch { setSelectedEnquiry(row.raw); } }} className="inline-flex items-center rounded-md border border-blue-300 px-3 py-1 text-xs font-medium text-blue-700 hover:bg-blue-50">View</button>
                  <button type="button" onClick={async () => { try { const full = await enquiriesApi.getEnquiry(row.id); await duplicateEnquiry(full); } catch { await duplicateEnquiry(row.raw); } }} className="inline-flex items-center rounded-md border border-green-300 px-3 py-1 text-xs font-medium text-green-700 hover:bg-green-50 ml-1">Duplicate</button></td></tr>))}
              </tbody>
            </table>
          </div>
          <div className="flex flex-col gap-3 border-t border-gray-200 px-6 py-4 text-sm text-gray-600 md:flex-row md:items-center md:justify-between">
            <div>
              {filteredRows.length === 0 ? (
                'No enquiries to show.'
              ) : (
                `Showing ${Math.min((safePage - 1) * pageSize + 1, filteredRows.length)}–${Math.min(safePage * pageSize, filteredRows.length)} of ${filteredRows.length}`
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <label className="text-xs text-gray-500">Rows per page</label>
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                className="rounded-md border border-gray-300 px-2 py-1 text-sm"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={safePage === 1}
                  className="rounded-md border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Prev
                </button>
                <span className="px-2 text-xs text-gray-600">Page {safePage} of {totalPages}</span>
                <button
                  type="button"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={safePage === totalPages}
                  className="rounded-md border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>

      {selectedEnquiry && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-2 md:p-4">
          <div className="w-full max-w-5xl rounded-lg bg-white shadow-xl max-h-[95vh] flex flex-col">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <h3 className="text-lg font-semibold text-gray-900">Enquiry Details</h3>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => openEditEnquiry(selectedEnquiry)}
                  className="inline-flex items-center rounded-md border border-blue-300 px-3 py-1 text-xs font-medium text-blue-700 hover:bg-blue-50"
                >
                  Edit Enquiry
                </button>
                {(selectedEnquiry.Status === 'CLOSED' || selectedEnquiry.DerivedStatus === 'Closed') ? (
                  <button type="button" onClick={handleReopenEnquiry} disabled={closingBusy}
                    className="inline-flex items-center rounded-md border border-green-300 px-3 py-1 text-xs font-medium text-green-700 hover:bg-green-50">
                    Reopen
                  </button>
                ) : (
                  <button type="button" onClick={() => setShowClosePicker((v) => !v)}
                    className="inline-flex items-center rounded-md border border-amber-300 px-3 py-1 text-xs font-medium text-amber-700 hover:bg-amber-50">
                    Mark Closed
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setSelectedEnquiry(null)}
                  className="text-sm text-gray-600 hover:text-gray-900"
                >
                  Close
                </button>
              </div>
            {showClosePicker && (
              <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg flex flex-wrap items-end gap-2">
                <div className="flex-1 min-w-[200px]">
                  <label className="block text-xs text-amber-800 mb-1">Reason for closing</label>
                  <select value={closingReason} onChange={(e) => setClosingReason(e.target.value)}
                    className="w-full px-2 py-1.5 border border-amber-300 rounded-md text-sm">
                    <option value="">Select a reason...</option>
                    <option value="Customer No Response">Customer No Response</option>
                    <option value="Requirement Cancelled">Requirement Cancelled</option>
                    <option value="Not Viable / Not Quoting">Not Viable / Not Quoting</option>
                    <option value="Lost to Competitor">Lost to Competitor</option>
                    <option value="Duplicate">Duplicate</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <button type="button" onClick={handleCloseEnquiry} disabled={closingBusy || !closingReason}
                  className="px-4 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded-md text-sm disabled:opacity-50">
                  {closingBusy ? 'Closing...' : 'Confirm Close'}
                </button>
                <button type="button" onClick={() => { setShowClosePicker(false); setClosingReason(''); }}
                  className="px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-md">
                  Cancel
                </button>
              </div>
            )}
            </div>
            <div className="max-h-[75vh] overflow-y-auto p-6">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div><p className="text-xs text-gray-500">Enquiry ID</p><p className="text-sm font-medium text-gray-900">{selectedEnquiry.id || '-'}</p></div>
                <div><p className="text-xs text-gray-500">Customer</p><p className="text-sm font-medium text-gray-900">{selectedEnquiry.AccountName || '-'}</p></div>
                <div><p className="text-xs text-gray-500">Status</p><p className="text-sm font-medium text-gray-900">{selectedEnquiry.DerivedStatus || selectedEnquiry.Status || '-'}</p></div>
                <div><p className="text-xs text-gray-500">Owner</p><p className="text-sm font-medium text-gray-900">{selectedEnquiry.OwnerName || selectedEnquiry.OwnerECode || '-'}</p></div>
                <div><p className="text-xs text-gray-500">Sale Type</p><p className="text-sm font-medium text-gray-900">{selectedEnquiry.BusinessType || '-'}</p></div>
                <div><p className="text-xs text-gray-500">Offer No.</p><p className="text-sm font-medium text-gray-900">{selectedEnquiry.OfferNo || '-'}</p></div>
              </div>

              <div className="mt-6">
                <h4 className="mb-2 text-sm font-semibold text-gray-900">Product Lines</h4>
                <div className="overflow-x-auto rounded border border-gray-200">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500">Product</th>
                        <th className="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500">Grade</th>
                        <th className="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500">Size</th>
                        <th className="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500">Qty</th>
                        <th className="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500">UT</th>
                        <th className="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500">Surface</th>
                        <th className="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500">Incoterms</th>
                        <th className="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500">Payment Terms</th>
                        <th className="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500">Price</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {((selectedEnquiry.Items || selectedEnquiry.items) || []).length === 0 && (
                        <tr>
                          <td colSpan={9} className="px-3 py-4 text-center text-sm text-gray-500">No product lines</td>
                        </tr>
                      )}
                      {((selectedEnquiry.Items || selectedEnquiry.items) || []).map((item, idx) => {
                        const terms = splitIncotermsAndPayment(item);
                        return (
                          <tr key={`${selectedEnquiry.id}-${idx}`}>
                            <td className="px-3 py-2 text-sm text-gray-700">{item.Product || item.product || '-'}</td>
                            <td className="px-3 py-2 text-sm text-gray-700">{item.Grade || item.grade || '-'}</td>
                            <td className="px-3 py-2 text-sm text-gray-700">{item.Dia || item.size_mm || '-'}</td>
                            <td className="px-3 py-2 text-sm text-gray-700">{item.Qty || item.qty || '-'} {item.UOM || item.uom || ''}</td>
                            <td className="px-3 py-2 text-sm text-gray-700">{item.UT || item.ultrasonic_test || '-'}</td>
                            <td className="px-3 py-2 text-sm text-gray-700">{item.Surface || item.surface || '-'}</td>
                            <td className="px-3 py-2 text-sm text-gray-700">{terms.incoterms}</td>
                            <td className="px-3 py-2 text-sm text-gray-700">{terms.paymentTerms}</td>
                            <td className="px-3 py-2 text-sm text-gray-700">{item.PriceOffer || item.price_offer || '-'} {item.PriceUom || item.price_uom || ''}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="mt-6">
                <h4 className="mb-2 text-sm font-semibold text-gray-900">Price Negotiation</h4>
                <p className="mb-3 text-xs text-gray-500">Every price is kept — our quotes and the customer's counters. Nothing is overwritten.</p>
                {enqPrices.length === 0 ? (
                  <p className="text-sm text-gray-400">No prices logged yet for this enquiry.</p>
                ) : (
                  <div className="rounded border border-gray-200 divide-y mb-3">
                    {enqPrices.map((row) => {
                      const label = row.price_type === 'COUNTER' ? 'Customer Counter' : row.price_type === 'FINAL' ? 'Final Agreed' : 'Our Quote';
                      const color = row.price_type === 'COUNTER' ? 'text-amber-700' : row.price_type === 'FINAL' ? 'text-green-700' : 'text-blue-700';
                      return (
                        <div key={row.id} className="flex items-center justify-between px-3 py-2 text-sm">
                          <div>
                            <span className={`font-medium ${color}`}>{label}</span>
                            {row.note && <span className="ml-2 text-xs text-gray-500">{row.note}</span>}
                          </div>
                          <div className="text-right">
                            <div className="font-semibold text-gray-900">₹{Number(row.price).toLocaleString('en-IN')}{row.price_uom ? ` ${row.price_uom}` : ''}</div>
                            <div className="text-xs text-gray-400">{String(row.created_at).slice(0,10)}{row.noted_by_name ? ` · ${row.noted_by_name}` : ''}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                <div className="flex flex-wrap items-end gap-2">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Price (INR/kg)</label>
                    <input type="number" step="0.01" value={enqNewPrice} onChange={(e) => setEnqNewPrice(e.target.value)} placeholder="e.g. 86" className="w-28 px-2 py-1.5 border border-gray-300 rounded-md text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Type</label>
                    <select value={enqNewType} onChange={(e) => setEnqNewType(e.target.value)} className="px-2 py-1.5 border border-gray-300 rounded-md text-sm">
                      <option value="QUOTED">Our Quote</option>
                      <option value="COUNTER">Customer Counter</option>
                      <option value="FINAL">Final Agreed</option>
                    </select>
                  </div>
                  <div className="flex-1 min-w-[140px]">
                    <label className="block text-xs text-gray-500 mb-1">Note (optional)</label>
                    <input type="text" value={enqNewNote} onChange={(e) => setEnqNewNote(e.target.value)} placeholder="e.g. competitor at 84" className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm" />
                  </div>
                  <button onClick={handleEnqLogPrice} disabled={enqLogging || !enqNewPrice} className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm disabled:opacity-50">
                    {enqLogging ? 'Saving...' : '+ Log Price'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900">
                {editingEnquiry ? 'Edit Enquiry' : 'Create Enquiry'}
              </h3>
              <button onClick={closeForm} className="text-gray-500 hover:text-gray-700 text-lg">X</button>
            </div>

            <div className="p-6">
              {draftBanner && (
                <div className="mb-4 flex items-center justify-between rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm">
                  <div className="text-blue-900">
                    <span className="mr-2">📝</span>
                    You have unsaved enquiry data from {Math.max(1, Math.round((Date.now() - draftBanner.savedAt) / 60000))} minute(s) ago.
                  </div>
                  <div className="flex gap-2">
                    <button onClick={restoreDraft} className="rounded-md bg-blue-600 px-3 py-1 text-xs font-medium text-white hover:bg-blue-700">Restore</button>
                    <button onClick={discardDraft} className="rounded-md border border-blue-300 bg-white px-3 py-1 text-xs font-medium text-blue-700 hover:bg-blue-50">Discard</button>
                  </div>
                </div>
              )}
              {mastersError && (
                <div className="mb-4 rounded-lg border border-orange-200 bg-orange-50 px-4 py-3 text-sm text-orange-800">
                  {mastersError}
                </div>
              )}
              {isMastersLoading && (
                <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
                  Loading grade and tolerance masters...
                </div>
              )}
              {(saleType === 'LOCAL' || saleType === 'EXPORT') && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Sale Type <span className="text-red-500">*</span></label>
                      <select
                        value={saleType}
                        onChange={(e) => {
                          setSaleType(e.target.value);
                          setFieldErrors((prev) => ({ ...prev, saleType: '' }));
                        }}
                        className={getInputClass('saleType')}
                      >
                        {SALE_TYPES.map((item) => (
                          <option key={item.id} value={item.id}>{item.label}</option>
                        ))}
                      </select>
                      {fieldErrors.saleType && <p className="mt-1 text-xs text-red-600">{fieldErrors.saleType}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Offer No. (auto)</label>
                      <input value={localForm.offerNo} readOnly className="w-full border border-gray-300 bg-gray-50 rounded-lg px-3 py-2 text-sm" />
                    </div>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-gray-800 mb-3">Customer Details</h4>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                      <div>
                        <FieldLabel required>Customer Name</FieldLabel>
                        <p className="text-xs text-gray-500 mb-1">Search and select — or type new name to create.</p>
                        <AccountSearchInput
                          value={localForm.accountId || ''}
                          initialName={localForm.customerName || ''}
                          onChange={(id, name) => {
                            setLocalForm((p) => ({ ...p,
                              customerName: name || p.customerName,
                              accountId: id || null
                            }));
                            setFieldErrors((prev) => ({ ...prev, customerName: '' }));
                          }}
                          placeholder="Customer Name *"
                          required
                          allowNew={true}
                          onNewCompany={(name) => {
                            setLocalForm((p) => ({ ...p, customerName: name, accountId: null }));
                            setFieldErrors((prev) => ({ ...prev, customerName: '' }));
                          }}
                          onContactsLoaded={(contacts, autoSelect) => {
                            if (contacts.length === 1 || autoSelect) {
                              const c = contacts[0];
                              setLocalForm((p) => ({
                                ...p,
                                contactPerson: c.Name || c.name || p.contactPerson,
                                email: c.Email1 || c.email_1 || p.email,
                                phone: c.Phone1 || c.phone_1 || p.phone,
                              }));
                            }
                          }}
                        />
                        {fieldErrors.customerName && <p className="mt-1 text-xs text-red-600">{fieldErrors.customerName}</p>}
                      </div>
                      <div>
                        <FieldLabel required>Contact Person</FieldLabel>
                        <p className="text-xs text-gray-500 mb-1">We’ll auto-create the contact for this account.</p>
                        <input value={localForm.contactPerson} onChange={(e) => {
                          setLocalForm((p) => ({ ...p, contactPerson: normalizeTextValue(e.target.value) }));
                          setFieldErrors((prev) => ({ ...prev, contactPerson: '' }));
                        }} placeholder="Contact Person *" className={getInputClass('contactPerson')} />
                        {fieldErrors.contactPerson && <p className="mt-1 text-xs text-red-600">{fieldErrors.contactPerson}</p>}
                      </div>
                      <div>
                        <FieldLabel required>Email</FieldLabel>
                        <input type="email" value={localForm.email} onChange={(e) => {
                          setLocalForm((p) => ({ ...p, email: e.target.value.trim() }));
                          setFieldErrors((prev) => ({ ...prev, email: '' }));
                        }} placeholder="Email" className={getInputClass('email')} />
                        {fieldErrors.email && <p className="mt-1 text-xs text-red-600">{fieldErrors.email}</p>}
                      </div>
                      <div>
                        <FieldLabel required>Phone</FieldLabel>
                        <input value={localForm.phone} onChange={(e) => {
                          setLocalForm((p) => ({ ...p, phone: e.target.value }));
                          setFieldErrors((prev) => ({ ...prev, phone: '' }));
                        }} placeholder="Phone" className={getInputClass('phone')} />
                        {fieldErrors.phone && <p className="mt-1 text-xs text-red-600">{fieldErrors.phone}</p>}
                      </div>
                      <div>
                        <FieldLabel>Sales Coordinator</FieldLabel>
                        <input
                          value={localForm.salesperson}
                          readOnly
                          placeholder="Logged-in user"
                          className="w-full rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-700"
                        />
                      </div>
                      <div>
                        <FieldLabel>Sales Manager</FieldLabel>
                        <select
                          value={localForm.kamEcode}
                          onChange={(e) => setLocalForm((p) => ({ ...p, kamEcode: e.target.value }))}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                        >
                          <option value="">
                            {isSalesManagersLoading ? 'Loading sales managers...' : 'Select Sales Manager'}
                          </option>
                          {salesManagers.map((manager) => (
                            <option key={manager.ECode || manager.ecode} value={manager.ECode || manager.ecode}>
                              {manager.Name || manager.name} ({manager.ECode || manager.ecode})
                            </option>
                          ))}
                        </select>
                        {salesManagersError && (
                          <p className="mt-1 text-xs text-red-600">{salesManagersError}</p>
                        )}
                      </div>
                      <div>
                        <FieldLabel>Approved By</FieldLabel>
                        <select
                          value={localForm.approvedByEcode}
                          onChange={(e) => setLocalForm((p) => ({ ...p, approvedByEcode: e.target.value }))}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                        >
                          <option value="">Select Approver</option>
                          {salesManagers.map((manager) => (
                            <option key={manager.ECode || manager.ecode} value={manager.ECode || manager.ecode}>
                              {manager.Name || manager.name} ({manager.ECode || manager.ecode})
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <FieldLabel required>Enquiry Date</FieldLabel>
                        <input type="date" value={localForm.enquiryDate} onChange={(e) => {
                          setLocalForm((p) => ({ ...p, enquiryDate: e.target.value }));
                        }} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                      </div>
                      <div>
                        <FieldLabel required>Follow-up Date</FieldLabel>
                        <input type="date" value={localForm.followupDate} onChange={(e) => {
                          setLocalForm((p) => ({ ...p, followupDate: e.target.value }));
                          setFieldErrors((prev) => ({ ...prev, followupDate: '' }));
                        }} className={getInputClass('followupDate')} />
                        {fieldErrors.followupDate && <p className="mt-1 text-xs text-red-600">{fieldErrors.followupDate}</p>}
                      </div>
                      <div>
                        <FieldLabel>Lead Source</FieldLabel>
                        <select
                          value={localForm.leadSource}
                          onChange={(e) => setLocalForm((p) => ({ ...p, leadSource: e.target.value }))}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                        >
                          {LEAD_SOURCES.map((source) => (
                            <option key={source} value={source}>{source}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-semibold text-gray-800">Product Details</h4>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={openImportDialog}
                          className="px-3 py-1.5 rounded border border-gray-300 text-gray-700 text-xs font-medium hover:bg-gray-50"
                        >
                          Import from Excel
                        </button>
                        <button type="button" onClick={addProductLine} className="px-3 py-1.5 rounded bg-blue-600 text-white text-xs font-medium">
                          + Add Product Line
                        </button>
                      </div>
                    </div>
                    <div className="space-y-4">
                      {productLines.map((line, idx) => (
                        <div key={idx} className="border border-gray-200 rounded-lg p-3">
                          <div className="mb-3 flex items-center justify-between">
                            <p className="text-xs font-semibold text-gray-700">Product Line {idx + 1}</p>
                            <div className="flex items-center gap-3">
                              <button
                                type="button"
                                onClick={() => duplicateProductLine(idx)}
                                className="text-xs font-medium text-blue-600 hover:text-blue-700"
                              >
                                Duplicate
                              </button>
                              <button
                                type="button"
                                onClick={() => removeProductLine(idx)}
                                disabled={productLines.length <= 1}
                                title="Remove product line"
                                className="inline-flex items-center text-red-600 hover:text-red-700 disabled:cursor-not-allowed disabled:text-gray-300"
                              >
                                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                  <path d="M3 6h18" />
                                  <path d="M8 6V4h8v2" />
                                  <path d="M19 6l-1 14H6L5 6" />
                                  <path d="M10 11v6" />
                                  <path d="M14 11v6" />
                                </svg>
                              </button>
                            </div>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                            <div>
                              <FieldLabel required>Product</FieldLabel>
                              <input list={listIds.product} value={line.product} onChange={(e) => {
                                updateProductLine(idx, 'product', e.target.value);
                                if (idx === 0) setFieldErrors((prev) => ({ ...prev, product: '' }));
                              }} placeholder="Select Product" className={getInputClass('product')} />
                            </div>
                            <div>
                              <FieldLabel required>Grade</FieldLabel>
                              <div className="relative">
                                <input
                                  value={line.grade}
                                  onFocus={() => setOpenGradeIndex(idx)}
                                  onBlur={() => setTimeout(() => setOpenGradeIndex(null), 150)}
                                  onChange={(e) => {
                                    updateProductLine(idx, 'grade', e.target.value);
                                    if (idx === 0) setFieldErrors((prev) => ({ ...prev, grade: '' }));
                                  }}
                                  placeholder="Type or Select Grade"
                                  className={getInputClass('grade')}
                                />
                                {openGradeIndex === idx && availableGradeOptions.length > 0 && (
                                  <div className="absolute z-20 mt-1 max-h-48 w-full overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg">
                                    {displayGradeOptions
                                      .filter((g) => {
                                        const label = getGradeOptionLabel(g).toLowerCase();
                                        return label.includes(String(line.grade || '').trim().toLowerCase());
                                      })
                                      .slice(0, 50)
                                      .map((g, optionIdx) => {
                                        const optionValue = typeof g === 'string'
                                          ? g.trim()
                                          : (g?.code || '').trim() || (g?.name || '').trim();
                                        if (!optionValue) return null;
                                        return (
                                          <button
                                            type="button"
                                            key={`${optionValue}-${optionIdx}`}
                                            className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                                            onMouseDown={() => {
                                              updateProductLine(idx, 'grade', optionValue);
                                              if (idx === 0) setFieldErrors((prev) => ({ ...prev, grade: '' }));
                                              setOpenGradeIndex(null);
                                            }}
                                          >
                                            {getGradeOptionLabel(g)}
                                          </button>
                                        );
                                      })}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div>
                              <FieldLabel>Specification</FieldLabel>
                              <input
                                value={line.specification}
                                onChange={(e) => {
                                  updateProductLine(idx, 'specification', e.target.value);
                                  if (idx === 0) setFieldErrors((prev) => ({ ...prev, specification: '' }));
                                }}
                                placeholder="Specification"
                                className={getInputClass('specification')}
                              />
                            </div>
                            <div>
                              <FieldLabel>Shape</FieldLabel>
                              <input
                                list={listIds.shape}
                                value={line.shape}
                                onChange={(e) => updateProductLine(idx, 'shape', e.target.value)}
                                placeholder="Shape"
                                className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full"
                              />
                            </div>
                            <div>
                              <FieldLabel required>Size (mm)</FieldLabel>
                              <input value={line.sizeMm} onChange={(e) => {
                                updateProductLine(idx, 'sizeMm', e.target.value);
                                if (idx === 0) setFieldErrors((prev) => ({ ...prev, sizeMm: '' }));
                              }} placeholder="Size (mm)" className={getInputClass('sizeMm')} />
                            </div>
                            <div>
                              <FieldLabel>Length (mm)</FieldLabel>
                              <input value={line.lengthMm} onChange={(e) => updateProductLine(idx, 'lengthMm', normalizeLengthValue(e.target.value))} placeholder="Length (mm) or 4000-4100" className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full" />
                            </div>
                            <div>
                              <FieldLabel required>Heat Treatment</FieldLabel>
                              <input list={listIds.heatTreatment} value={line.heatTreatment} onChange={(e) => {
                                updateProductLine(idx, 'heatTreatment', e.target.value);
                                if (idx === 0) setFieldErrors((prev) => ({ ...prev, heatTreatment: '' }));
                              }} placeholder="Heat Treatment" className={getInputClass('heatTreatment')} />
                            </div>
                            <div>
                              <FieldLabel required>Tolerance</FieldLabel>
                              <input list={listIds.tolerance} value={line.tolerance} onChange={(e) => {
                                updateProductLine(idx, 'tolerance', e.target.value);
                                if (idx === 0) setFieldErrors((prev) => ({ ...prev, tolerance: '' }));
                              }} placeholder="Tolerance" className={getInputClass('tolerance')} />
                            </div>
                            <div>
                              <FieldLabel>Chamfering</FieldLabel>
                              <input list={listIds.chamfering} value={line.chamfering} onChange={(e) => updateProductLine(idx, 'chamfering', e.target.value)} placeholder="Chamfering" className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full" />
                            </div>
                            <div>
                              <FieldLabel>Qty (tons)</FieldLabel>
                              <input
                                type="text"
                                inputMode="decimal"
                                value={line.qty}
                                onBeforeInput={(e) => {
                                  const data = e.data;
                                  if (data !== '.' && data !== ',') return;
                                  // iOS/Android sometimes only fires beforeinput for punctuation.
                                  e.preventDefault();
                                  const el = e.currentTarget;
                                  const start = el.selectionStart ?? el.value.length;
                                  const end = el.selectionEnd ?? el.value.length;
                                  const nextRaw = `${el.value.slice(0, start)}.${el.value.slice(end)}`;
                                  const cleaned = nextRaw.replace(/[^0-9.]/g, '');
                                  const [whole, ...rest] = cleaned.split('.');
                                  const normalized = rest.length ? `${whole}.${rest.join('')}` : whole;
                                  updateProductLine(idx, 'qty', normalized);
                                  if (idx === 0) setFieldErrors((prev) => ({ ...prev, qty: '' }));
                                  requestAnimationFrame(() => {
                                    const pos = Math.min(start + 1, (el.value || '').length + 1);
                                    el.setSelectionRange(pos, pos);
                                  });
                                }}
                                onKeyDown={(e) => {
                                  if (e.key !== '.' && e.key !== ',') return;
                                  // Manually insert decimal separator for keyboards that don't emit it onChange.
                                  e.preventDefault();
                                  const el = e.currentTarget;
                                  const start = el.selectionStart ?? el.value.length;
                                  const end = el.selectionEnd ?? el.value.length;
                                  const nextRaw = `${el.value.slice(0, start)}.${el.value.slice(end)}`;
                                  const cleaned = nextRaw.replace(/[^0-9.]/g, '');
                                  const [whole, ...rest] = cleaned.split('.');
                                  const normalized = rest.length ? `${whole}.${rest.join('')}` : whole;
                                  updateProductLine(idx, 'qty', normalized);
                                  if (idx === 0) setFieldErrors((prev) => ({ ...prev, qty: '' }));
                                  requestAnimationFrame(() => {
                                    const pos = Math.min(start + 1, (el.value || '').length + 1);
                                    el.setSelectionRange(pos, pos);
                                  });
                                }}
                                onPaste={(e) => {
                                  const text = e.clipboardData?.getData('text') || '';
                                  const withDot = text.replace(',', '.');
                                  const cleaned = withDot.replace(/[^0-9.]/g, '');
                                  if (!cleaned) return;
                                  e.preventDefault();
                                  updateProductLine(idx, 'qty', cleaned);
                                  if (idx === 0) setFieldErrors((prev) => ({ ...prev, qty: '' }));
                                }}
                                onChange={(e) => {
                                  // Allow both "." and "," as decimal separator.
                                  const raw = e.target.value;
                                  const withDot = raw.replace(',', '.');
                                  const cleaned = withDot.replace(/[^0-9.]/g, '');
                                  const [whole, ...rest] = cleaned.split('.');
                                  const normalized = rest.length ? `${whole}.${rest.join('')}` : whole;
                                  updateProductLine(idx, 'qty', normalized);
                                  if (idx === 0) setFieldErrors((prev) => ({ ...prev, qty: '' }));
                                }}
                                placeholder="Qty"
                                className={getInputClass('qty')}
                              />
                            </div>
                            <div>
                              <FieldLabel>Unit</FieldLabel>
                              <input list={listIds.unit} value={line.unit} onChange={(e) => updateProductLine(idx, 'unit', e.target.value)} placeholder="Unit" className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full" />
                            </div>
                            <div>
                              <FieldLabel>Ultrasonic Test</FieldLabel>
                              <select value={line.ultrasonicTest || ''} onChange={(e) => updateProductLine(idx, 'ultrasonicTest', e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full bg-white">
                                <option value="">— Select —</option>
                                <option value="Required">Required</option>
                                <option value="Not Required">Not Required</option>
                              </select>
                            </div>
                            <div>
                              <FieldLabel>Surface</FieldLabel>
                              <input list={listIds.surface} value={line.surface} onChange={(e) => updateProductLine(idx, 'surface', e.target.value)} placeholder="Surface" className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full" />
                            </div>
                            <div>
                              <FieldLabel>Incoterms</FieldLabel>
                              <input list={listIds.incoterms} value={line.incoterms} onChange={(e) => updateProductLine(idx, 'incoterms', e.target.value)} placeholder="Incoterms" className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full" />
                            </div>
                            <div>
                              <FieldLabel>Price {line.priceUnit ? `(${line.priceUnit})` : "(/kg)"}</FieldLabel>
                              <input value={line.priceOffer} onChange={(e) => {
                                updateProductLine(idx, 'priceOffer', e.target.value);
                                if (idx === 0) setFieldErrors((prev) => ({ ...prev, priceOffer: '' }));
                              }} placeholder="Price" className={getInputClass('priceOffer')} />
                            </div>
                            <div>
                              <FieldLabel>Price Unit</FieldLabel>
                              <input list={listIds.priceUnit} value={line.priceUnit} onChange={(e) => updateProductLine(idx, 'priceUnit', e.target.value)} placeholder="Price Unit" className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full" />
                            </div>
                          </div>
                          {idx === 0 && (
                            <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-2">
                              {['product', 'grade', 'specification', 'sizeMm', 'lengthMm', 'heatTreatment', 'tolerance', 'qty', 'priceOffer', 'incoterms'].map((key) =>
                                fieldErrors[key] ? (
                                  <p key={key} className="text-xs text-red-600">{fieldErrors[key]}</p>
                                ) : null
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-gray-800 mb-3">Commercial</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <FieldLabel required>Validity</FieldLabel>
                        <input
                          value={localForm.validityDays}
                          onChange={(e) => {
                            setLocalForm((p) => ({ ...p, validityDays: e.target.value }));
                            setFieldErrors((prev) => ({ ...prev, validityDays: '' }));
                          }}
                          placeholder="Validity (e.g., 30 Days / 6 Weeks)"
                          className={getInputClass('validityDays')}
                        />
                        {fieldErrors.validityDays && <p className="mt-1 text-xs text-red-600">{fieldErrors.validityDays}</p>}
                      </div>
                      <div>
                        <FieldLabel required>Payment Terms</FieldLabel>
                        <input list={listIds.paymentTerms} value={localForm.paymentTerms} onChange={(e) => {
                          setLocalForm((p) => ({ ...p, paymentTerms: e.target.value }));
                          setFieldErrors((prev) => ({ ...prev, paymentTerms: '' }));
                        }} placeholder="Payment Terms *" className={getInputClass('paymentTerms')} />
                        {fieldErrors.paymentTerms && <p className="mt-1 text-xs text-red-600">{fieldErrors.paymentTerms}</p>}
                      </div>
                      <div>
                        <FieldLabel>Packing</FieldLabel>
                        <input value={localForm.packing} onChange={(e) => setLocalForm((p) => ({ ...p, packing: e.target.value }))} placeholder="Packing" className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full" />
                      </div>
                      <div>
                        <FieldLabel>Freight</FieldLabel>
                        <input value={localForm.freight} onChange={(e) => setLocalForm((p) => ({ ...p, freight: e.target.value }))} placeholder="Freight" className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full" />
                      </div>
                      <div>
                        <FieldLabel>Prepared By</FieldLabel>
                        <select value={localForm.preparedBy} onChange={(e) => setLocalForm((p) => ({ ...p, preparedBy: e.target.value }))} className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full">
                          <option value="">Select</option>
                          {scUsers.map((u) => (<option key={u.ecode || u.ECode} value={u.name || u.Name}>{u.name || u.Name}</option>))}
                        </select>
                      </div>
                      <div>
                        <FieldLabel>Delivery Terms</FieldLabel>
                        <input value={localForm.deliveryTerms} onChange={(e) => setLocalForm((p) => ({ ...p, deliveryTerms: e.target.value }))} placeholder="Delivery Terms" className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full" />
                      </div>
                      {saleType === 'EXPORT' && (
                        <div>
                          <FieldLabel required>Port</FieldLabel>
                          <input
                            value={localForm.port}
                            onChange={(e) => {
                              setLocalForm((p) => ({ ...p, port: e.target.value }));
                              setFieldErrors((prev) => ({ ...prev, port: '' }));
                            }}
                            placeholder="Port *"
                            className={getInputClass('port')}
                          />
                          {fieldErrors.port && <p className="mt-1 text-xs text-red-600">{fieldErrors.port}</p>}
                        </div>
                      )}
                    </div>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-gray-800 mb-3">Notes</h4>
                    <textarea
                      value={localForm.specialNotes}
                      onChange={(e) => setLocalForm((p) => ({ ...p, specialNotes: e.target.value }))}
                      placeholder="Special comments or notes for this enquiry"
                      className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full min-h-[90px]"
                    />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-gray-800 mb-3">Delivery Note</h4>
                    <textarea
                      value={localForm.deliveryNote}
                      onChange={(e) => setLocalForm((p) => ({ ...p, deliveryNote: e.target.value }))}
                      placeholder="Delivery revision or special delivery instructions"
                      className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full min-h-[70px]"
                    />
                  </div>
                </div>
              )}

              <datalist id={listIds.product}>{LOCAL_DROPDOWNS.product.map((v) => <option key={v} value={v} />)}</datalist>
              <datalist id={listIds.heatTreatment}>{LOCAL_DROPDOWNS.heatTreatment.map((v) => <option key={v} value={v} />)}</datalist>
              <datalist id={listIds.tolerance}>{toleranceClassOptions.map((v) => <option key={v} value={String(v).toUpperCase()} />)}</datalist>
              <datalist id={listIds.chamfering}>{LOCAL_DROPDOWNS.chamfering.map((v) => <option key={v} value={v} />)}</datalist>
              <datalist id={listIds.unit}>{LOCAL_DROPDOWNS.unit.map((v) => <option key={v} value={v} />)}</datalist>
              <datalist id={listIds.ultrasonicTest}>{LOCAL_DROPDOWNS.ultrasonicTest.map((v) => <option key={v} value={v} />)}</datalist>
              <datalist id={listIds.surface}>{LOCAL_DROPDOWNS.surface.map((v) => <option key={v} value={v} />)}</datalist>
              <datalist id={listIds.incoterms}>{LOCAL_DROPDOWNS.incoterms.map((v) => <option key={v} value={v} />)}</datalist>
              <datalist id={listIds.priceUnit}>{LOCAL_DROPDOWNS.priceUnit.map((v) => <option key={v} value={v} />)}</datalist>
              <datalist id={listIds.paymentTerms}>{LOCAL_DROPDOWNS.paymentTerms.map((v) => <option key={v} value={v} />)}</datalist>
              <datalist id={listIds.orderProgress}>{LOCAL_DROPDOWNS.orderProgress.map((v) => <option key={v} value={v} />)}</datalist>

              <div className="mt-6 flex justify-end gap-3">
                <button type="button" onClick={closeForm} className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm">
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveEnquiry}
                  disabled={isSaving}
                  className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isSaving ? 'Saving...' : editingEnquiry ? 'Update Enquiry' : 'Save Enquiry'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Duplicate Account Modal */}
      {dupModal && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 px-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
              <div className="flex items-start gap-3 mb-4">
                <div className="flex-shrink-0 w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">Similar account already exists</h3>
                  <p className="text-xs text-gray-500 mt-0.5">We found accounts that may match <strong>{dupModal.companyName}</strong></p>
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 mb-4 space-y-2 max-h-40 overflow-y-auto">
                {dupModal.matches.map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => dupModal.onUse(m.id)}
                    className="w-full text-left px-3 py-2 rounded-md bg-white border border-gray-200 hover:border-blue-400 hover:bg-blue-50 transition-colors"
                  >
                    <span className="text-sm font-medium text-gray-800">{m.name}</span>
                    <span className="text-xs text-gray-400 ml-2">ID #{m.id}</span>
                  </button>
                ))}
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={dupModal.onForce}
                  className="flex-1 px-3 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Create as New Account
                </button>
                <button
                  type="button"
                  onClick={dupModal.onCancel}
                  className="px-3 py-2 text-xs font-medium text-gray-500 hover:text-gray-700 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

      {importState.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-2xl rounded-lg bg-white shadow-xl">
            <div className="border-b border-gray-200 px-5 py-4 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-800">Import Product Lines</h3>
              <button type="button" onClick={closeImportDialog} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>
            <div className="px-5 py-4 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-2">Upload Excel</label>
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={(e) => handleImportFile(e.target.files?.[0])}
                  className="block w-full text-sm text-gray-700"
                />
                {importState.filename && (
                  <p className="mt-1 text-xs text-gray-500">Loaded: {importState.filename}</p>
                )}
                {importState.error && (
                  <p className="mt-1 text-xs text-red-600">{importState.error}</p>
                )}
              </div>

              {importState.headers.length > 0 && (
                <div className="space-y-3">
                  <p className="text-xs text-gray-500">Map your columns to CRM fields:</p>
                  {importState.rawRows.length > 0 && (
                    <div className="grid grid-cols-3 items-center gap-3">
                      <span className="text-xs text-gray-700">Header Row</span>
                      <select
                        value={importState.headerRowIndex}
                        onChange={(e) => {
                          const nextIndex = Number(e.target.value);
                          const { headers, bodyRows, mapping } = applyHeaderSelection(importState.rawRows, nextIndex);
                          setImportState((prev) => ({
                            ...prev,
                            headerRowIndex: nextIndex,
                            headers,
                            rows: bodyRows,
                            mapping,
                          }));
                        }}
                        className="col-span-2 rounded-md border border-gray-300 px-2 py-1 text-sm"
                      >
                        {importState.rawRows.slice(0, 10).map((row, idx) => (
                          <option key={`header-row-${idx}`} value={idx}>
                            Row {idx + 1} {row?.length ? `(${row.slice(0, 3).map((cell) => String(cell || '').trim()).filter(Boolean).join(' / ')})` : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  {[
                    { key: 'material', label: 'Material / Product' },
                    { key: 'grade', label: 'Grade' },
                    { key: 'size', label: 'Size / Dia' },
                    { key: 'length', label: 'Length' },
                    { key: 'qty', label: 'Qty / Weight' },
                    { key: 'price', label: 'Price' },
                    { key: 'unit', label: 'Unit' },
                    { key: 'heat_treatment', label: 'Heat Treatment' },
                    { key: 'surface', label: 'Surface' },
                    { key: 'specification', label: 'Specification/Hardness' },
                  ].map((field) => (
                    <div key={field.key} className="grid grid-cols-3 items-center gap-3">
                      <span className="text-xs text-gray-700">{field.label}</span>
                      <select
                        value={importState.mapping[field.key] ?? ''}
                        onChange={(e) =>
                          setImportState((prev) => ({
                            ...prev,
                            mapping: { ...prev.mapping, [field.key]: e.target.value === '' ? null : Number(e.target.value) },
                          }))
                        }
                        className="col-span-2 rounded-md border border-gray-300 px-2 py-1 text-sm"
                      >
                        <option value="">-- Not mapped --</option>
                        {importState.headers.map((h, idx) => (
                          <option key={`${h}-${idx}`} value={idx}>
                            {h || `Column ${idx + 1}`}
                          </option>
                        ))}
                      </select>
                    </div>
                  ))}
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-gray-500">Rows detected: {importState.rows.length}</p>
                    <button
                      type="button"
                      onClick={() =>
                        setImportState((prev) => ({
                          ...prev,
                          mapping: buildDefaultMapping(prev.headers),
                        }))
                      }
                      className="text-xs text-blue-600 hover:text-blue-700"
                    >
                      Auto map
                    </button>
                  </div>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 border-t border-gray-200 px-5 py-4">
              <button type="button" onClick={closeImportDialog} className="px-3 py-1.5 rounded border border-gray-300 text-sm text-gray-700">
                Cancel
              </button>
              <button
                type="button"
                onClick={applyImport}
                className="px-3 py-1.5 rounded bg-blue-600 text-white text-sm font-medium"
              >
                Import Lines
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Enquiries;
